import { Fragment, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CircleMarker, MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import config from "../config";
import { useTranslation } from "react-i18next";

const createCareMapIcon = ({ className, label, title }) =>
  L.divIcon({
    className: "care-map-marker-shell",
    html: `
      <div class="care-map-marker ${className}" aria-label="${title}">
        <span>${label}</span>
      </div>
    `,
    iconSize: [46, 54],
    iconAnchor: [23, 50],
    popupAnchor: [0, -48]
  });

const greenIcon = createCareMapIcon({
  className: "care-map-marker-available",
  label: "A",
  title: "Available caregiver"
});

const redIcon = createCareMapIcon({
  className: "care-map-marker-unavailable",
  label: "U",
  title: "Unavailable caregiver"
});

const blackIcon = createCareMapIcon({
  className: "care-map-marker-elder",
  label: "E",
  title: "Elder location"
});

const COUNTRY_ABBREVIATIONS = {
  Uganda: "UG",
  Kenya: "KE",
  USA: "US",
  UK: "UK",
  Canada: "CA",
  India: "IN"
};

const getCountryAbbreviation = (countryName = "") => {
  if (COUNTRY_ABBREVIATIONS[countryName]) {
    return COUNTRY_ABBREVIATIONS[countryName];
  }

  const words = String(countryName).trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return words.slice(0, 2).map((word) => word[0]).join("").toUpperCase();
  }

  return String(countryName).slice(0, 2).toUpperCase() || "GL";
};

const createCountryClusterIcon = (countryName, count) =>
  L.divIcon({
    className: "country-cluster-icon-wrapper",
    html: `
      <div class="country-cluster-icon" aria-label="${countryName}: ${count} caregivers">
        <span class="country-cluster-code">${getCountryAbbreviation(countryName)}</span>
        <span class="country-cluster-count">${count}</span>
        <span class="country-cluster-caption">caregivers</span>
      </div>
    `,
    iconSize: [62, 62],
    iconAnchor: [31, 31]
  });

const FILTER_TYPES = [
  { label: "Language", value: "language" },
  { label: "Gender", value: "gender" },
  { label: "Country", value: "country" },
  { label: "Maximum rate", value: "rate" },
  { label: "Availability", value: "availability" }
];

const LANGUAGE_OPTIONS = ["English", "Spanish", "French", "Swahili", "Arabic", "Portuguese", "Chinese", "Hindi", "German"];
const FALLBACK_TIME_ZONES = [
  "Africa/Kampala",
  "Africa/Nairobi",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "UTC"
];

const getSupportedTimeZones = () => {
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      return Intl.supportedValuesOf("timeZone");
    }
  } catch {
    return FALLBACK_TIME_ZONES;
  }

  return FALLBACK_TIME_ZONES;
};

const TIME_ZONE_OPTIONS = getSupportedTimeZones();

const COUNTRY_TIME_ZONES = {
  uganda: "Africa/Kampala",
  kenya: "Africa/Nairobi",
  usa: "America/New_York",
  us: "America/New_York",
  "united states": "America/New_York",
  uk: "Europe/London",
  "united kingdom": "Europe/London",
  canada: "America/Toronto",
  india: "Asia/Kolkata",
  rwanda: "Africa/Kigali",
  tanzania: "Africa/Dar_es_Salaam"
};

const USA_STATE_TIME_ZONES = {
  california: "America/Los_Angeles",
  ca: "America/Los_Angeles",
  "new york": "America/New_York",
  ny: "America/New_York",
  texas: "America/Chicago",
  tx: "America/Chicago",
  florida: "America/New_York",
  fl: "America/New_York",
  illinois: "America/Chicago",
  il: "America/Chicago",
  arizona: "America/Phoenix",
  az: "America/Phoenix",
  colorado: "America/Denver",
  co: "America/Denver",
  washington: "America/Los_Angeles",
  wa: "America/Los_Angeles"
};

const normalizeTimeZoneLookupValue = (value = "") =>
  String(value).trim().toLowerCase().replace(/\./g, "").replace(/\s+/g, " ");

const getTimeZoneForLocation = (country = "", stateProvince = "") => {
  const normalizedCountry = normalizeTimeZoneLookupValue(country);
  const normalizedState = normalizeTimeZoneLookupValue(stateProvince);

  if (["usa", "us", "united states"].includes(normalizedCountry)) {
    return USA_STATE_TIME_ZONES[normalizedState] || "America/New_York";
  }

  return COUNTRY_TIME_ZONES[normalizedCountry] || "";
};

const getCaregiverTimeZone = (worker = {}) =>
  worker.workerTimeZone ||
  worker.deviceTimeZone ||
  getTimeZoneForLocation(worker.country, worker.stateProvince) ||
  "UTC";

const toLocalDateTimeInputValue = (date) => {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const getMinimumAppointmentTime = () => {
  const minimum = new Date(Date.now() + 5 * 60 * 1000);
  minimum.setSeconds(0, 0);
  return toLocalDateTimeInputValue(minimum);
};

const getBrowserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};

const createSuggestedAppointmentTime = ({ dayOffset = 0, hour, hoursFromNow }) => {
  const date = new Date();
  if (hoursFromNow) {
    date.setTime(date.getTime() + hoursFromNow * 60 * 60 * 1000);
    date.setMinutes(Math.ceil(date.getMinutes() / 15) * 15, 0, 0);
  } else {
    date.setDate(date.getDate() + dayOffset);
    date.setHours(hour, 0, 0, 0);
  }
  return toLocalDateTimeInputValue(date);
};

const parseDateTimeInputParts = (value = "") => {
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return null;

  const [, year, month, day, hour, minute] = match;
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute)
  };
};

const getTimeZoneOffsetMs = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23"
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const zonedAsUtc = Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    Number(values.hour),
    Number(values.minute),
    Number(values.second)
  );

  return zonedAsUtc - date.getTime();
};

const parseDateTimeInputInTimeZone = (value, timeZone = getBrowserTimeZone() || "UTC") => {
  const parts = parseDateTimeInputParts(value);
  if (!parts) return null;

  const localAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    0
  );

  try {
    let utcTime = localAsUtc - getTimeZoneOffsetMs(new Date(localAsUtc), timeZone);
    utcTime = localAsUtc - getTimeZoneOffsetMs(new Date(utcTime), timeZone);
    return new Date(utcTime);
  } catch {
    const fallbackDate = new Date(value);
    return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
  }
};

const formatDateInTimeZone = (date, timeZone) => {
  if (!date || Number.isNaN(date.getTime())) return "";

  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone,
      timeZoneName: "short"
    }).format(date);
  } catch {
    return "";
  }
};

const formatAppointmentSelection = (value) => {
  const parts = parseDateTimeInputParts(value);
  if (!parts) return "";

  const displayDate = new Date(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    0,
    0
  );
  if (Number.isNaN(displayDate.getTime())) return "";

  return new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(displayDate);
};

const getDeviceClockText = () =>
  new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(new Date());

const validateAppointmentDateTime = (value, selectedTimeZone = getBrowserTimeZone()) => {
  if (!value) return "Choose a date and time for the WhatsApp call.";

  const selectedDate = parseDateTimeInputInTimeZone(value, selectedTimeZone);
  if (!selectedDate || Number.isNaN(selectedDate.getTime())) {
    return "Enter a real date and time for the WhatsApp call.";
  }

  if (selectedDate.getTime() <= Date.now() + 60 * 1000) {
    return `Choose a future time at least one minute from now. This device currently shows ${getDeviceClockText()}.`;
  }

  return "";
};

const getDateTimeInputValueForTimeZone = (isoValue, timeZone) => {
  const date = isoValue ? new Date(isoValue) : null;
  if (!date || Number.isNaN(date.getTime())) return "";

  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timeZone || getBrowserTimeZone() || "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23"
    }).formatToParts(date);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));

    return `${values.year}-${values.month}-${values.day}T${values.hour}:${values.minute}`;
  } catch {
    return toLocalDateTimeInputValue(date);
  }
};

const getAppointmentLocalInputValue = (appointment = {}, timeZone = "") => {
  const value = appointment.scheduledForText || appointment.scheduledFor;
  return getDateTimeInputValueForTimeZone(value, timeZone);
};
const GENDER_OPTIONS = [
  { label: "Male", value: "M" },
  { label: "Female", value: "F" }
];
const COUNTRY_OPTIONS = ["Uganda", "Kenya", "USA", "UK", "Canada", "India"];
const RATE_OPTIONS = [...Array(6)].map((_, i) => i + 1);
const APPOINTMENT_ALERT_WINDOW_MS = 15 * 60 * 1000;
const APPOINTMENT_SOON_WINDOW_MS = 10 * 60 * 1000;
const ELDER_MAP_REFRESH_MS = 5 * 60 * 1000;
const CAREGIVER_REVISION_CHECK_MS = 5 * 1000;
const ELDER_APPOINTMENT_REFRESH_MS = 30 * 1000;

const getFilterOptions = (filterType) => {
  if (filterType === "language") {
    return LANGUAGE_OPTIONS.map((language) => ({ label: language, value: language }));
  }

  if (filterType === "gender") {
    return GENDER_OPTIONS;
  }

  if (filterType === "country") {
    return COUNTRY_OPTIONS.map((country) => ({ label: country, value: country }));
  }

  if (filterType === "rate") {
    return RATE_OPTIONS.map((rate) => ({
      label: `Up to $${rate}/hr`,
      value: rate
    }));
  }

  if (filterType === "availability") {
    return [
      { label: "Available now", value: "available" },
      { label: "Unavailable", value: "unavailable" }
    ];
  }

  return [];
};

const compactFilters = (nextFilters) =>
  Object.fromEntries(
    Object.entries(nextFilters).filter(([, value]) => String(value || "").trim())
  );

const getFilterValueLabel = (filterType, value) =>
  getFilterOptions(filterType).find((option) => String(option.value) === String(value))?.label ||
  value;

const getAppointmentTime = (appointment = {}) => {
  const value = appointment.scheduledForText || appointment.scheduledFor;
  const parsed = value ? new Date(value) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed.getTime() : null;
};

const getAppointmentAlertStatus = (appointment, now = Date.now()) => {
  const appointmentTime = getAppointmentTime(appointment);
  if (!appointmentTime) return "";

  const delta = appointmentTime - now;
  if (delta <= 0 && Math.abs(delta) <= APPOINTMENT_ALERT_WINDOW_MS) return "now";
  if (delta > 0 && delta <= APPOINTMENT_SOON_WINDOW_MS) return "soon";
  return "";
};

const statusMeta = {
  available: {
    label: "Available now",
    tone: "#15803d",
    chipBackground: "rgba(21, 128, 61, 0.12)"
  },
  unavailable: {
    label: "Currently unavailable",
    tone: "#b91c1c",
    chipBackground: "rgba(185, 28, 28, 0.12)"
  }
};

const getYouTubeEmbedUrl = (value) => {
  if (!value) return "";

  try {
    const url = new URL(value);
    const hostname = url.hostname.replace(/^www\./, "").toLowerCase();

    if (hostname === "youtu.be") {
      const id = url.pathname.split("/").filter(Boolean)[0];
      return id ? `https://www.youtube-nocookie.com/embed/${id}?rel=0` : "";
    }

    if (hostname.endsWith("youtube.com")) {
      if (url.pathname === "/watch") {
        const id = url.searchParams.get("v");
        return id ? `https://www.youtube-nocookie.com/embed/${id}?rel=0` : "";
      }

      const [, type, id] = url.pathname.split("/");
      if ((type === "shorts" || type === "embed") && id) {
        return `https://www.youtube-nocookie.com/embed/${id}?rel=0`;
      }
    }
  } catch {
    return "";
  }

  return "";
};

const buildLocationParts = (locationOwner) =>
  [
    locationOwner.city,
    locationOwner.district || locationOwner.stateProvince,
    locationOwner.country
  ].filter(Boolean);

const getLocationLabel = (locationOwner) => buildLocationParts(locationOwner).join(", ");

const getWorkerDisplayName = (worker = {}) =>
  `${worker.firstName || ""} ${worker.lastName || ""}`.replace(/\s+/g, " ").trim() ||
  "Caregiver";

const getStableMarkerKey = (item = {}) =>
  String(item.markerID || item.workerID || item.patientID || item.country || getWorkerDisplayName(item));

const getCoordinateKey = (latitude, longitude) =>
  `${Number(latitude).toFixed(6)}:${Number(longitude).toFixed(6)}`;

const getOffsetPosition = (latitude, longitude, index, total) => {
  if (total <= 1) {
    return [Number(latitude), Number(longitude)];
  }

  const angle = (Math.PI * 2 * index) / total;
  const radius = 0.008 + Math.floor(index / 8) * 0.004;
  const safeLatitude = Number(latitude);
  const latitudeOffset = Math.sin(angle) * radius;
  const longitudeOffset =
    (Math.cos(angle) * radius) / Math.max(Math.cos((safeLatitude * Math.PI) / 180), 0.35);

  return [safeLatitude + latitudeOffset, Number(longitude) + longitudeOffset];
};

const spreadOverlappingMarkers = (items) => {
  const grouped = items.reduce((acc, item) => {
    const key = getCoordinateKey(item.latitude, item.longitude);

    if (!acc[key]) {
      acc[key] = [];
    }

    acc[key].push(item);
    return acc;
  }, {});

  return Object.values(grouped).flatMap((group) =>
    [...group].sort((left, right) =>
      getStableMarkerKey(left).localeCompare(getStableMarkerKey(right))
    ).map((item, index) => {
      const overlapGroupKey = getCoordinateKey(item.latitude, item.longitude);
      const [latitude, longitude] = getOffsetPosition(
        item.latitude,
        item.longitude,
        index,
        group.length
      );

      return {
        ...item,
        markerLatitude: latitude,
        markerLongitude: longitude,
        overlapCount: group.length,
        overlapIndex: index,
        originLatitude: Number(item.latitude),
        originLongitude: Number(item.longitude),
        overlapGroupKey,
        isOverlapping: group.length > 1
      };
    })
  );
};

const buildOverlapGuides = (markers) =>
  Object.values(
    markers.reduce((acc, marker) => {
      if (marker.markerType !== "worker" || !marker.isOverlapping) {
        return acc;
      }

      if (!acc[marker.overlapGroupKey]) {
        acc[marker.overlapGroupKey] = {
          key: marker.overlapGroupKey,
          count: marker.overlapCount,
          center: [marker.originLatitude, marker.originLongitude],
          members: []
        };
      }

      acc[marker.overlapGroupKey].members.push(marker);
      return acc;
    }, {})
  );

const buildCountryClusters = (workers) =>
  Object.values(
    workers.reduce((acc, worker) => {
      const countryName = worker.country || "Other";
      if (!acc[countryName]) {
        acc[countryName] = {
          country: countryName,
          members: []
        };
      }

      acc[countryName].members.push(worker);
      return acc;
    }, {})
  ).map((cluster) => {
    const center = cluster.members.reduce(
      (sum, worker) => ({
        latitude: sum.latitude + Number(worker.latitude),
        longitude: sum.longitude + Number(worker.longitude)
      }),
      { latitude: 0, longitude: 0 }
    );

    return {
      ...cluster,
      latitude: center.latitude / cluster.members.length,
      longitude: center.longitude / cluster.members.length
    };
  });

const ZoomControls = () => {
  const map = useMap();

  return (
    <div style={zoomControls}>
      <button type="button" style={zoomBtn} onClick={() => map.zoomIn()}>
        +
      </button>
      <button type="button" style={zoomBtn} onClick={() => map.zoomOut()}>
        -
      </button>
    </div>
  );
};

const MapViewportController = ({ focusPoints }) => {
  const map = useMap();

  useEffect(() => {
    if (!Array.isArray(focusPoints) || focusPoints.length === 0) return;

    if (focusPoints.length === 1) {
      map.flyTo(focusPoints[0], 6, { duration: 0.8 });
      return;
    }

    map.flyToBounds(L.latLngBounds(focusPoints), {
      padding: [48, 48],
      maxZoom: 7,
      duration: 0.8
    });
  }, [focusPoints, map]);

  return null;
};

const WorkerPopup = ({
  worker,
  loadingWorkerID,
  onToggleActivation,
  onSchedule,
  schedulerWorkerID,
  schedulerProps,
  onWatchIntro,
  renderStars,
  t
}) => {
  const [showMore, setShowMore] = useState(false);
  const isAvailable = worker.status === "available";
  const meta = isAvailable ? statusMeta.available : statusMeta.unavailable;
  const initials = `${worker.firstName?.[0] || ""}${worker.lastName?.[0] || ""}`.toUpperCase();
  const location = getLocationLabel(worker);
  const whatsappNumber = String(worker.whatsapp || "").replace(/[^\d]/g, "");
  const genderLabel =
    worker.gender === "M"
      ? "Male"
      : worker.gender === "F"
        ? "Female"
        : worker.gender || t("map:popup.notListed");
  const rateLabel =
    worker.rate || worker.rate === 0 ? `$${worker.rate}/hr` : t("map:popup.notListed");
  const specialties = typeof worker.specialties === "string"
    ? worker.specialties
    : Array.isArray(worker.specialties)
      ? worker.specialties.filter(Boolean).join(", ")
      : "";
  const certifications = typeof worker.certifications === "string"
    ? worker.certifications
    : Array.isArray(worker.certifications)
      ? worker.certifications.filter(Boolean).join(", ")
      : "";
  const experienceHighlights = Array.isArray(worker.experienceEntries)
    ? worker.experienceEntries.filter(Boolean)
    : [];
  const isApproximateLocation =
    worker.locationPrecision &&
    !["city/district", "district"].includes(worker.locationPrecision);

  return (
    <div className="worker-popup-card">
      <div className="worker-popup-hero">
        {worker.profilePhoto ? (
          <img
            src={worker.profilePhoto}
            alt={`${worker.firstName || ""} ${worker.lastName || ""}`.trim() || "Worker"}
            className="worker-avatar worker-photo"
          />
        ) : (
          <div className="worker-avatar">{initials || "CW"}</div>
        )}
        <div>
          <div className="worker-name">
            {worker.firstName} {worker.lastName}
          </div>
          <div className="worker-role">
            {worker.professionalTitle || t("map:popup.remoteCaregiver")}
          </div>
        </div>
        <span
          className="worker-status-chip"
          style={{ color: meta.tone, background: meta.chipBackground }}
        >
          {meta.label}
        </span>
      </div>

      <div className="worker-popup-grid">
        <div className="worker-popup-stat">
          <span>{t("map:popup.language")}</span>
          <strong>{worker.spokenLanguage || t("map:popup.notListed")}</strong>
        </div>
        <div className="worker-popup-stat">
          <span>{t("map:popup.gender")}</span>
          <strong>{genderLabel}</strong>
        </div>
        <div className="worker-popup-stat">
          <span>{t("map:popup.rate")}</span>
          <strong>{rateLabel}</strong>
        </div>
        <div className="worker-popup-stat">
          <span>{t("map:popup.location")}</span>
          <strong>{location || t("map:popup.locationNotProvided")}</strong>
          {isApproximateLocation && (
            <small className="worker-popup-location-note">
              Map pin is approximate at {worker.locationPrecision} level.
            </small>
          )}
        </div>
        <div className="worker-popup-stat">
          <span>{t("map:popup.contact")}</span>
          <strong>
            {!isAvailable
              ? "Hidden while unavailable"
              : worker.whatsapp || t("map:popup.phoneNotProvided")}
          </strong>
        </div>
      </div>

      <div className="worker-popup-section">
        <div className="worker-popup-label">{t("map:popup.familyFeedback")}</div>
        <div className="worker-rating-row">
          <div>{renderStars(worker.rating)}</div>
          <span className="worker-rating-copy">{t("map:popup.ratingValue", { rating: Number(worker.rating) || 0 })}</span>
        </div>
      </div>

      {worker.introVideoUrl && (
        <button
          type="button"
          className="worker-secondary-action"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onWatchIntro(worker);
          }}
        >
          Watch caregiver intro
        </button>
      )}

      <button
        type="button"
        className="worker-more-toggle"
        aria-expanded={showMore}
        onClick={() => setShowMore((prev) => !prev)}
      >
        <span>{showMore ? "Hide extra details" : "See full caregiver details"}</span>
        <span className={`worker-more-toggle-icon${showMore ? " is-open" : ""}`} aria-hidden="true">
          {showMore ? "˄" : "˅"}
        </span>
      </button>

      {showMore && worker.bio && (
        <div className="worker-popup-section">
          <div className="worker-popup-label">About</div>
          <div className="worker-popup-text">{worker.bio}</div>
        </div>
      )}

      {showMore && (
        <>
          <div className="worker-popup-grid worker-popup-grid-expanded">
            <div className="worker-popup-stat">
              <span>{t("map:popup.payment")}</span>
              <strong>{worker.paymentMethod || t("map:popup.flexible")}</strong>
            </div>
            <div className="worker-popup-stat">
              <span>Experience</span>
              <strong>
                {worker.yearsExperience ? `${worker.yearsExperience} years` : t("map:popup.notListed")}
              </strong>
            </div>
            <div className="worker-popup-stat">
              <span>Certifications</span>
              <strong>{certifications || t("map:popup.notListed")}</strong>
            </div>
            <div className="worker-popup-stat">
              <span>{t("map:popup.serviceArea")}</span>
              <strong>{location || t("map:popup.locationNotProvided")}</strong>
            </div>
          </div>

          {specialties && (
            <div className="worker-popup-section">
              <div className="worker-popup-label">Specialties</div>
              <div className="worker-popup-text">{specialties}</div>
            </div>
          )}

          {worker.availabilityNotes && (
            <div className="worker-popup-section">
              <div className="worker-popup-label">Availability notes</div>
              <div className="worker-popup-text">{worker.availabilityNotes}</div>
            </div>
          )}

          {experienceHighlights.length > 0 && (
            <div className="worker-popup-section">
              <div className="worker-popup-label">Work history</div>
              <div className="worker-popup-list">
                {experienceHighlights.map((entry, index) => (
                  <div key={`${worker.workerID}-exp-${index}`} className="worker-popup-list-item">
                    {typeof entry === "string" ? entry : entry?.title || entry?.role || entry?.summary || JSON.stringify(entry)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {isAvailable && whatsappNumber ? (
        <a
          href={`https://wa.me/${whatsappNumber}`}
          target="_blank"
          rel="noreferrer"
          className="worker-primary-action"
        >
          {t("map:popup.contactWhatsapp")}
        </a>
      ) : (
        <button type="button" disabled className="worker-secondary-action worker-disabled">
          {t("map:popup.workerUnavailableBtn")}
        </button>
      )}

      <button
        type="button"
        className="worker-secondary-action"
        style={{
          background: worker.activated
            ? "linear-gradient(135deg, #dc3545, #b91c1c)"
            : "linear-gradient(135deg, #15803d, #16a34a)",
          opacity: loadingWorkerID === worker.workerID ? 0.75 : 1
        }}
        disabled={loadingWorkerID === worker.workerID}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onToggleActivation(worker.workerID, worker.activated);
        }}
      >
        {loadingWorkerID === worker.workerID
          ? t("map:popup.updating")
          : worker.activated
            ? t("map:popup.deactivateWorker")
            : t("map:popup.activateWorker")}
      </button>

      {worker.activated && isAvailable && (
        <>
          <button
            type="button"
            className="worker-secondary-action"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onSchedule(worker);
            }}
          >
            Schedule WhatsApp call
          </button>

          {schedulerWorkerID === worker.workerID && schedulerProps && (
            <AppointmentScheduler
              {...schedulerProps}
              worker={worker}
              compact
              panelStyle={popupAppointmentPanel}
            />
          )}
        </>
      )}

      {worker.activated && !isAvailable && (
        <div className="worker-availability-guard" role="status">
          Scheduling is paused until this caregiver marks themselves available.
        </div>
      )}
    </div>
  );
};

const PatientPopup = ({ patient, t }) => {
  const location = getLocationLabel(patient);

  return (
    <div className="worker-popup-card patient-popup-card">
      <div className="worker-popup-hero">
        <div className="worker-avatar patient-avatar">{t("map:popup.you")}</div>
        <div>
          <div className="worker-name">
            {patient.firstName} {patient.lastName}
          </div>
          <div className="worker-role">{t("map:popup.yourBase")}</div>
        </div>
      </div>

      <div className="worker-popup-grid">
        <div className="worker-popup-stat">
          <span>{t("map:popup.language")}</span>
          <strong>{patient.spokenLanguage || t("map:popup.notListed")}</strong>
        </div>
        <div className="worker-popup-stat">
          <span>{t("map:popup.location")}</span>
          <strong>{location || t("map:popup.locationUnknown")}</strong>
        </div>
      </div>
    </div>
  );
};

const ActivatedCaregiversPanel = ({
  caregivers,
  expanded,
  onToggleExpanded,
  loadingWorkerID,
  onSchedule,
  onFocus,
  onDeactivate
}) => (
  <div style={activatedPanel}>
    <button
      type="button"
      style={activatedHeaderButton}
      onClick={onToggleExpanded}
      aria-expanded={expanded}
      aria-controls="elder-activated-caregivers-list"
    >
      <div>
        <div style={activatedEyebrow}>My caregivers</div>
        <h3 style={activatedTitle}>Caregivers you saved</h3>
      </div>
      <span style={activatedHeaderMeta}>
        <span style={activatedCountBadge}>{caregivers.length}</span>
        <span style={activatedChevron} aria-hidden="true">
          {expanded ? "⌃" : "⌄"}
        </span>
      </span>
    </button>

    {expanded && (
      caregivers.length ? (
      <div id="elder-activated-caregivers-list" style={activatedList}>
        {caregivers.map((caregiver) => {
          const isAvailable = caregiver.status !== "unavailable";
          const isUpdating = loadingWorkerID === caregiver.workerID;

          return (
            <div key={caregiver.workerID} style={activatedCard}>
              <div>
                <strong style={activatedName}>{getWorkerDisplayName(caregiver)}</strong>
                <span style={activatedMeta}>
                  {getLocationLabel(caregiver) || caregiver.country || "Location not listed"}
                </span>
                <span
                  style={{
                    ...activatedStatus,
                    color: isAvailable ? "#166534" : "#991b1b",
                    background: isAvailable ? "#dcfce7" : "#fee2e2"
                  }}
                >
                  {isAvailable ? "Available" : "Unavailable"}
                </span>
              </div>

              <div style={activatedActions}>
                <button
                  type="button"
                  style={activatedPrimaryButton}
                  disabled={!isAvailable}
                  onClick={() => onSchedule(caregiver)}
                >
                  Schedule
                </button>
                <button
                  type="button"
                  style={activatedSecondaryButton}
                  onClick={() => onFocus(caregiver)}
                >
                  View
                </button>
                <button
                  type="button"
                  style={activatedDangerButton}
                  disabled={isUpdating}
                  onClick={() => onDeactivate(caregiver)}
                >
                  {isUpdating ? "Removing..." : "Deactivate"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <p style={activatedEmpty}>
        Activated caregivers will appear here so the elder can remember who they saved.
      </p>
      )
    )}
  </div>
);

const AppointmentNotifications = ({
  appointments,
  urgentAppointmentID,
  alertMessage,
  lastSyncedAt,
  syncError,
  onCancelAppointment,
  cancellingAppointmentID,
  onStartEditAppointment,
  editingAppointmentID,
  editScheduledFor,
  editAppointmentTimeZone,
  editAppointmentNotes,
  editAppointmentMessage,
  updatingAppointmentID,
  onEditScheduledForChange,
  onEditAppointmentTimeZoneChange,
  onEditNotesChange,
  onSubmitAppointmentEdit,
  onCloseAppointmentEdit,
  deviceTimeZone
}) => (
  <div style={appointmentPanel}>
    <div style={appointmentEyebrow}>Appointments</div>
    <h3 style={appointmentTitle}>Scheduled meeting details</h3>

    <div style={alertHealthCard}>
      <span
        style={{
          ...alertHealthDot,
          background: "#16a34a"
        }}
      />
      <div style={alertHealthCopy}>
        <strong>Email alerts are active</strong>
        <span>
          {syncError
            ? "Appointment connection needs attention."
            : `You and the caregiver are emailed when the appointment is scheduled and ten minutes before the call${lastSyncedAt ? ` · synced ${lastSyncedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}.`}
        </span>
      </div>
    </div>

    {alertMessage && <div style={appointmentAlertBox}>{alertMessage}</div>}

    {appointments.length ? (
      <div style={appointmentList}>
        {appointments.slice(0, 4).map((appointment) => {
          const whatsappNumber = String(appointment.workerWhatsapp || "").replace(/[^\d]/g, "");
          const alertStatus = getAppointmentAlertStatus(appointment);

          return (
            <div
              key={appointment.appointmentID}
              style={{
                ...appointmentCard,
                ...(appointment.appointmentID === urgentAppointmentID ? urgentAppointmentCard : {}),
                ...(alertStatus === "soon" ? soonAppointmentCard : {})
              }}
            >
              {appointment.appointmentID === urgentAppointmentID && (
                <span style={urgentBadge}>Time for appointment</span>
              )}
              {alertStatus === "soon" && (
                <span style={soonBadge}>Starting soon</span>
              )}
              <strong style={appointmentCaregiver}>
                {appointment.workerFirstName} {appointment.workerLastName}
              </strong>
              <span style={appointmentTime}>
                {appointment.patientScheduledForText ||
                  formatAppointmentDate(appointment.scheduledForText)}
              </span>
              {appointment.caregiverScheduledEmailSentAt && (
                <span style={emailDeliveryBadge}>Caregiver notified by email</span>
              )}
              {appointment.elderScheduledEmailSentAt && (
                <span style={emailDeliveryBadge}>Your confirmation email sent</span>
              )}
              {appointment.elderEmailReminderSentAt && (
                <span style={emailDeliveryBadge}>Your reminder email sent</span>
              )}
              {appointment.caregiverEmailReminderSentAt && (
                <span style={emailDeliveryBadge}>Caregiver reminder email sent</span>
              )}
              <p style={appointmentCopy}>
                WhatsApp care call
                {appointment.notes ? `: ${appointment.notes}` : ""}
              </p>
              {appointment.workerStatus === "unavailable" && (
                <div style={appointmentPausedNotice}>
                  Caregiver unavailable · calling is paused
                </div>
              )}
              {whatsappNumber && appointment.workerStatus !== "unavailable" && (
                <a
                  href={`https://wa.me/${whatsappNumber}`}
                  target="_blank"
                  rel="noreferrer"
                  style={appointmentAction}
                >
                  Open WhatsApp
                </a>
              )}
              <div style={appointmentButtonRow}>
                <button
                  type="button"
                  style={appointmentEditButton}
                  disabled={updatingAppointmentID === appointment.appointmentID}
                  onClick={() => onStartEditAppointment(appointment)}
                >
                  Edit time
                </button>
                <button
                  type="button"
                  style={appointmentCancelButton}
                  disabled={cancellingAppointmentID === appointment.appointmentID}
                  onClick={() => onCancelAppointment(appointment.appointmentID)}
                >
                  {cancellingAppointmentID === appointment.appointmentID
                    ? "Cancelling..."
                    : "Cancel"}
                </button>
              </div>
              {editingAppointmentID === appointment.appointmentID && (
                <AppointmentScheduler
                  worker={{
                    firstName: appointment.workerFirstName,
                    lastName: appointment.workerLastName,
                    country: appointment.workerCountry,
                    stateProvince: appointment.workerStateProvince,
                    workerTimeZone: appointment.workerTimeZone,
                    deviceTimeZone: appointment.workerDeviceTimeZone
                  }}
                  scheduledFor={editScheduledFor}
                  notes={editAppointmentNotes}
                  scheduling={updatingAppointmentID === appointment.appointmentID}
                  message={editAppointmentMessage}
                  onScheduledForChange={onEditScheduledForChange}
                  onNotesChange={onEditNotesChange}
                  onSubmit={(event) => onSubmitAppointmentEdit(event, appointment)}
                  onCancel={onCloseAppointmentEdit}
                  deviceTimeZone={deviceTimeZone}
                  appointmentTimeZone={editAppointmentTimeZone}
                  onAppointmentTimeZoneChange={onEditAppointmentTimeZoneChange}
                  compact
                  mode="edit"
                  panelStyle={appointmentInlineEditor}
                />
              )}
            </div>
          );
        })}
      </div>
    ) : (
      <p style={appointmentEmpty}>
        Appointment notifications from activated caregivers will appear here.
      </p>
    )}
  </div>
);

const AppointmentScheduler = ({
  worker,
  scheduledFor,
  notes,
  scheduling,
  message,
  onScheduledForChange,
  onNotesChange,
  onSubmit,
  onCancel,
  deviceTimeZone,
  appointmentTimeZone,
  appointmentTimeZoneManuallyChanged = false,
  onAppointmentTimeZoneChange,
  onUseDeviceTimeZone,
  compact = false,
  panelStyle = appointmentPanel,
  mode = "schedule"
}) => {
  const selectedTimeText = formatAppointmentSelection(scheduledFor);
  const isEditing = mode === "edit";
  const formID = `elder-appointment-${mode}-${compact ? "compact" : "panel"}`;
  const detectedTimeZone = deviceTimeZone || getBrowserTimeZone() || "UTC";
  const selectedTimeZone = appointmentTimeZone || detectedTimeZone;
  const caregiverTimeZone = getCaregiverTimeZone(worker);
  const selectedInstant = parseDateTimeInputInTimeZone(scheduledFor, selectedTimeZone);
  const caregiverPreviewText = formatDateInTimeZone(selectedInstant, caregiverTimeZone);
  const elderPreviewText = formatDateInTimeZone(selectedInstant, selectedTimeZone);
  const sameTimeZone = selectedTimeZone === caregiverTimeZone;
  const canResetToDeviceTimeZone =
    Boolean(onUseDeviceTimeZone) && selectedTimeZone !== detectedTimeZone;
  const timeZoneOptions = TIME_ZONE_OPTIONS.includes(selectedTimeZone)
    ? TIME_ZONE_OPTIONS
    : [selectedTimeZone, ...TIME_ZONE_OPTIONS];
  const suggestions = [
    { label: "In 1 hour", value: createSuggestedAppointmentTime({ hoursFromNow: 1 }) },
    { label: "Tomorrow morning", value: createSuggestedAppointmentTime({ dayOffset: 1, hour: 9 }) },
    { label: "Tomorrow afternoon", value: createSuggestedAppointmentTime({ dayOffset: 1, hour: 14 }) }
  ];

  return (
    <form
      className={`appointment-scheduler-form${compact ? " appointment-scheduler-form-compact" : ""}`}
      style={panelStyle}
      onSubmit={onSubmit}
      onClick={(event) => event.stopPropagation()}
      noValidate
    >
      <div style={appointmentEyebrow}>{isEditing ? "Edit care call" : "Schedule care call"}</div>
      <h3 style={compact ? appointmentCompactTitle : appointmentTitle}>
        {worker ? `${worker.firstName} ${worker.lastName}` : "Selected caregiver"}
      </h3>
      <p style={compact ? appointmentCompactCopy : appointmentCopy}>
        {isEditing
          ? "Choose the new date, time, and timezone. We keep the same appointment and email the caregiver the matching local time."
          : "Choose the date and time. We automatically use this device's timezone and convert it to the caregiver's local time."}
      </p>

      <label style={appointmentDateField} htmlFor={`${formID}-timezone`}>
        <span style={appointmentFormLabel}>
          Appointment timezone {isEditing ? "" : "(auto-detected)"}
        </span>
        <select
          id={`${formID}-timezone`}
          style={compact ? appointmentCompactInput : input}
          value={selectedTimeZone}
          onChange={(event) => onAppointmentTimeZoneChange(event.target.value)}
        >
          {timeZoneOptions.map((timeZone) => (
            <option key={timeZone} value={timeZone}>
              {timeZone.replace(/_/g, " ")}
            </option>
          ))}
        </select>
      </label>
      <div style={appointmentTimezoneHelp}>
        <span>
          Device timezone: {detectedTimeZone.replace(/_/g, " ")}.
          {appointmentTimeZoneManuallyChanged
            ? " You changed it manually for this appointment."
            : " We will use this automatically unless you change it."}
        </span>
        {canResetToDeviceTimeZone && (
          <button
            type="button"
            style={appointmentTimezoneResetButton}
            onClick={onUseDeviceTimeZone}
          >
            Use device timezone
          </button>
        )}
      </div>

      <label style={appointmentDateField} htmlFor={`${formID}-date-time`}>
        <span style={appointmentFormLabel}>Date and time</span>
        <input
          id={`${formID}-date-time`}
          type="datetime-local"
          min={getMinimumAppointmentTime()}
          step="60"
          style={compact ? appointmentCompactInput : input}
          value={scheduledFor}
          onChange={(event) => onScheduledForChange(event.target.value)}
        />
      </label>

      <div style={appointmentSuggestionWrap} aria-label="Suggested appointment times">
        {suggestions.map((suggestion) => (
          <button
            key={suggestion.label}
            type="button"
            style={appointmentSuggestionButton}
            onClick={() => onScheduledForChange(suggestion.value)}
          >
            {suggestion.label}
          </button>
        ))}
      </div>

      {selectedTimeText && (
        <div style={appointmentConfirmationCard}>
          <span style={appointmentConfirmationLabel}>You selected</span>
          <strong>{elderPreviewText || selectedTimeText}</strong>
          <span>
            Appointment timezone: {selectedTimeZone || "Detecting..."}
            {!isEditing && !appointmentTimeZoneManuallyChanged ? " (from this device)" : ""}
          </span>
          <span>Caregiver local time: {caregiverPreviewText || "Select a valid time"}</span>
          <span>Caregiver timezone: {caregiverTimeZone}</span>
          {sameTimeZone && (
            <span style={appointmentSameTimezoneNote}>
              These times match because the appointment timezone and caregiver timezone are the same.
            </span>
          )}
        </div>
      )}

      <label style={compact ? appointmentCompactNotesLabel : appointmentNotesLabel} htmlFor={`${formID}-notes`}>
        Notes
      </label>
      <textarea
        id={`${formID}-notes`}
        style={compact ? appointmentCompactTextarea : { ...input, minHeight: "74px", resize: "vertical" }}
        value={notes}
        onChange={(event) => onNotesChange(event.target.value)}
        placeholder="Example: Please call after lunch"
      />

      <div style={compact ? appointmentCompactButtonRow : buttonRow}>
        <button type="submit" style={compact ? appointmentCompactApplyBtn : applyBtn} disabled={scheduling}>
          {scheduling
            ? isEditing
              ? "Updating & notifying..."
              : "Scheduling & notifying..."
            : isEditing
              ? "Update & notify caregiver"
              : "Schedule & notify caregiver"}
        </button>
        <button type="button" style={compact ? appointmentCompactClearBtn : clearBtn} onClick={onCancel}>
          Close
        </button>
      </div>

      {message && <div style={compact ? appointmentCompactErrorBox : activationErrorBox}>{message}</div>}
    </form>
  );
};

const IntroVideoModal = ({ worker, onClose }) => {
  if (!worker) return null;

  const embedUrl = getYouTubeEmbedUrl(worker.introVideoUrl);

  return (
    <div style={videoBackdrop} onClick={onClose}>
      <div style={videoModal} onClick={(event) => event.stopPropagation()}>
        <div style={videoHeader}>
          <div>
            <div style={appointmentEyebrow}>Caregiver intro</div>
            <h3 style={videoTitle}>
              {worker.firstName} {worker.lastName}
            </h3>
          </div>
          <button type="button" style={videoCloseButton} onClick={onClose}>
            Close
          </button>
        </div>
        {embedUrl ? (
          <>
            <iframe
              title={`${worker.firstName || "Caregiver"} introduction`}
              src={embedUrl}
              style={videoFrame}
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
            <a
              href={worker.introVideoUrl}
              target="_blank"
              rel="noreferrer"
              style={videoExternalLink}
            >
              Open on YouTube
            </a>
          </>
        ) : (
          <a href={worker.introVideoUrl} target="_blank" rel="noreferrer" style={appointmentAction}>
            Open YouTube
          </a>
        )}
      </div>
    </div>
  );
};

const ElderMap = () => {
  const patientID = localStorage.getItem("patientID");
  const token = localStorage.getItem("token");
  const profileComplete = localStorage.getItem("profileComplete");
  const navigate = useNavigate();
  const { t } = useTranslation(["map", "common"]);
  const workerMarkerRefs = useRef({});
  const caregiverMapRevisionRef = useRef("");
  const mapRequestSequenceRef = useRef(0);

  const [draftFilters, setDraftFilters] = useState({
    language: "",
    gender: "",
    country: "",
    rate: "",
    availability: ""
  });
  const [nameSearch, setNameSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [patient, setPatient] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [activatedCaregivers, setActivatedCaregivers] = useState([]);
  const [showActivatedCaregivers, setShowActivatedCaregivers] = useState(false);
  const [loadingWorkerID, setLoadingWorkerID] = useState(null);
  const [activationError, setActivationError] = useState("");
  const [expandedCountry, setExpandedCountry] = useState("");
  const [focusPoints, setFocusPoints] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointmentWorker, setSelectedAppointmentWorker] = useState(null);
  const [scheduledFor, setScheduledFor] = useState("");
  const [appointmentTimeZone, setAppointmentTimeZone] = useState(getBrowserTimeZone);
  const [appointmentTimeZoneManuallyChanged, setAppointmentTimeZoneManuallyChanged] = useState(false);
  const [appointmentNotes, setAppointmentNotes] = useState("");
  const [schedulingAppointment, setSchedulingAppointment] = useState(false);
  const [appointmentMessage, setAppointmentMessage] = useState("");
  const [showDiscoveryWhileScheduling, setShowDiscoveryWhileScheduling] = useState(false);
  const [notificationAlertMessage, setNotificationAlertMessage] = useState("");
  const [lastAppointmentSync, setLastAppointmentSync] = useState(null);
  const [appointmentSyncError, setAppointmentSyncError] = useState(false);
  const [cancellingAppointmentID, setCancellingAppointmentID] = useState("");
  const [editingAppointmentID, setEditingAppointmentID] = useState("");
  const [editScheduledFor, setEditScheduledFor] = useState("");
  const [editAppointmentTimeZone, setEditAppointmentTimeZone] = useState(getBrowserTimeZone);
  const [editAppointmentNotes, setEditAppointmentNotes] = useState("");
  const [editAppointmentMessage, setEditAppointmentMessage] = useState("");
  const [updatingAppointmentID, setUpdatingAppointmentID] = useState("");
  const [urgentAppointmentID, setUrgentAppointmentID] = useState("");
  const [deviceTimeZone, setDeviceTimeZone] = useState(getBrowserTimeZone);
  const [introVideoWorker, setIntroVideoWorker] = useState(null);
  const [selectedWorkerID, setSelectedWorkerID] = useState("");
  const filterPanelRef = useRef(null);
  const mapSectionRef = useRef(null);
  const activeFilters = useMemo(() => Object.entries(filters), [filters]);

  useEffect(() => {
    if (!patientID || !token) {
      navigate("/elder-login");
      return;
    }

    if (profileComplete === "false") {
      navigate("/elder-profile-setup");
    }
  }, [navigate, patientID, profileComplete, token]);

  const renderStars = (rawRating) => {
    const rating = Math.max(0, Math.min(4, Number(rawRating) || 0));
    const maxStars = 4;

    return (
      <div style={{ color: "#d97706", fontSize: "18px", letterSpacing: "0.1em" }}>
        {"★".repeat(rating)}
        {"☆".repeat(maxStars - rating)}
      </div>
    );
  };

  const fetchMapData = useCallback(async () => {
    const requestSequence = ++mapRequestSequenceRef.current;

    try {
      const res = await axios.get(`${config.API_BASE_URL}/patient/map`, {
        params: { patientID, ...filters },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (requestSequence !== mapRequestSequenceRef.current) return;
      caregiverMapRevisionRef.current = res.data.caregiverMapRevision || "";
      setPatient(res.data.patient);
      const nextWorkers = res.data.workers || [];
      setWorkers(nextWorkers);
      setActivatedCaregivers(
        res.data.activatedCaregivers || nextWorkers.filter((worker) => worker.activated)
      );
    } catch {
      // Keep the last usable map visible during a temporary network interruption.
    }
  }, [patientID, filters, token]);

  const fetchCaregiverMapRevision = useCallback(async () => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/caregiver-temp-map-revision`
      );
      const nextRevision = response.data?.revision || "";

      if (
        nextRevision &&
        caregiverMapRevisionRef.current &&
        nextRevision !== caregiverMapRevisionRef.current
      ) {
        await fetchMapData();
      }
    } catch {
      // The five-minute full refresh remains as a low-cost fallback.
    }
  }, [fetchMapData]);

  const fetchAppointments = useCallback(() => {
    if (!patientID || !token) return;

    axios
      .get(`${config.API_BASE_URL}/patient/appointments`, {
        params: { patientID },
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((res) => {
        setAppointments(res.data.appointments || []);
        setLastAppointmentSync(new Date());
        setAppointmentSyncError(false);
      })
      .catch(() => setAppointmentSyncError(true));
  }, [patientID, token]);

  const syncDeviceTimeZone = useCallback(() => {
    if (!patientID || !token) return;
    const currentTimeZone = getBrowserTimeZone();
    if (!currentTimeZone) return;

    setDeviceTimeZone(currentTimeZone);
    if (!appointmentTimeZoneManuallyChanged) {
      setAppointmentTimeZone(currentTimeZone);
    }
    setEditAppointmentTimeZone((previous) => previous || currentTimeZone);
    void axios
      .patch(
        `${config.API_BASE_URL}/user/timezone`,
        { id: patientID, role: "patient", timeZone: currentTimeZone },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(fetchAppointments)
      .catch(() => {});
  }, [appointmentTimeZoneManuallyChanged, fetchAppointments, patientID, token]);

  useEffect(() => {
    const synchronizeWhenVisible = () => {
      if (document.visibilityState === "visible") syncDeviceTimeZone();
    };

    syncDeviceTimeZone();
    const interval = window.setInterval(syncDeviceTimeZone, 5 * 60 * 1000);
    window.addEventListener("focus", syncDeviceTimeZone);
    document.addEventListener("visibilitychange", synchronizeWhenVisible);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", syncDeviceTimeZone);
      document.removeEventListener("visibilitychange", synchronizeWhenVisible);
    };
  }, [syncDeviceTimeZone]);

  useEffect(() => {
    fetchMapData();
    fetchAppointments();
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") fetchMapData();
    }, ELDER_MAP_REFRESH_MS);
    const revisionInterval = window.setInterval(() => {
      if (document.visibilityState === "visible") fetchCaregiverMapRevision();
    }, CAREGIVER_REVISION_CHECK_MS);
    const appointmentInterval = window.setInterval(() => {
      if (document.visibilityState === "visible") fetchAppointments();
    }, ELDER_APPOINTMENT_REFRESH_MS);
    const refreshVisibleData = () => {
      if (document.visibilityState !== "visible") return;
      fetchMapData();
      fetchAppointments();
    };

    window.addEventListener("focus", refreshVisibleData);
    document.addEventListener("visibilitychange", refreshVisibleData);
    return () => {
      window.clearInterval(interval);
      window.clearInterval(revisionInterval);
      window.clearInterval(appointmentInterval);
      window.removeEventListener("focus", refreshVisibleData);
      document.removeEventListener("visibilitychange", refreshVisibleData);
    };
  }, [fetchAppointments, fetchCaregiverMapRevision, fetchMapData]);

  useEffect(() => {
    if (!expandedCountry) return;

    const countryStillVisible = workers.some(
      (worker) => (worker.country || "Other") === expandedCountry
    );
    if (!countryStillVisible) {
      setExpandedCountry("");
    }
  }, [expandedCountry, workers]);

  const toggleActivation = async (workerID, isActive) => {
    try {
      setLoadingWorkerID(workerID);
      setActivationError("");

      const endpoint = isActive
        ? "/patient/deactivate-worker"
        : "/patient/activate-worker";

      await axios.post(
        `${config.API_BASE_URL}${endpoint}`,
        { patientID, workerID },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setWorkers((previousWorkers) =>
        previousWorkers.map((worker) =>
          worker.workerID === workerID
            ? { ...worker, activated: !isActive }
            : worker
        )
      );
      setActivatedCaregivers((previousCaregivers) => {
        if (isActive) {
          return previousCaregivers.filter((caregiver) => caregiver.workerID !== workerID);
        }

        const activatedWorker = workers.find((worker) => worker.workerID === workerID);
        if (!activatedWorker || previousCaregivers.some((caregiver) => caregiver.workerID === workerID)) {
          return previousCaregivers;
        }

        setShowActivatedCaregivers(true);
        return [{ ...activatedWorker, activated: true }, ...previousCaregivers];
      });
      await fetchMapData();
      fetchAppointments();
      if (isActive && selectedAppointmentWorker?.workerID === workerID) {
        setSelectedAppointmentWorker(null);
        setShowDiscoveryWhileScheduling(false);
      }
    } catch (err) {
      console.error(err);
      setActivationError(
        err.response?.data?.error ||
          "Unable to update this caregiver connection right now."
      );
    } finally {
      setLoadingWorkerID(null);
    }
  };

  const applyFilter = () => {
    setFilters(compactFilters(draftFilters));
    setExpandedCountry("");
    setFocusPoints([]);
    setSelectedWorkerID("");
  };

  const clearFilter = () => {
    setDraftFilters({
      language: "",
      gender: "",
      country: "",
      rate: "",
      availability: ""
    });
    setNameSearch("");
    setFilters({});
    setExpandedCountry("");
    setFocusPoints([]);
    setSelectedWorkerID("");
  };

  const handleAppointmentTimeZoneChange = (timeZone) => {
    setAppointmentTimeZone(timeZone);
    setAppointmentTimeZoneManuallyChanged(true);
  };

  const useDeviceAppointmentTimeZone = () => {
    setAppointmentTimeZone(getBrowserTimeZone() || deviceTimeZone || "UTC");
    setAppointmentTimeZoneManuallyChanged(false);
  };

  const openScheduler = (worker) => {
    if (worker.status === "unavailable") {
      setAppointmentMessage("This caregiver is unavailable. Calling and scheduling are paused.");
      return;
    }
    setEditingAppointmentID("");
    setEditAppointmentMessage("");
    setSelectedAppointmentWorker(worker);
    setShowDiscoveryWhileScheduling(false);
    setAppointmentTimeZone(getBrowserTimeZone() || deviceTimeZone || "UTC");
    setAppointmentTimeZoneManuallyChanged(false);
    setAppointmentMessage("");
    setSelectedWorkerID(worker.workerID);
    setFocusPoints([[Number(worker.latitude), Number(worker.longitude)]]);
    window.requestAnimationFrame(() => {
      filterPanelRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const scheduleAppointment = async (event) => {
    event.preventDefault();

    if (!selectedAppointmentWorker?.workerID || !scheduledFor) {
      setAppointmentMessage("Choose a date and time for the WhatsApp call.");
      return;
    }

    const selectedSchedulingTimeZone = appointmentTimeZone || getBrowserTimeZone() || deviceTimeZone || "UTC";
    const validationMessage = validateAppointmentDateTime(scheduledFor, selectedSchedulingTimeZone);
    if (validationMessage) {
      setAppointmentMessage(validationMessage);
      return;
    }

    const currentWorker = workers.find(
      (worker) => worker.workerID === selectedAppointmentWorker.workerID
    );
    if (!currentWorker || currentWorker.status === "unavailable") {
      setAppointmentMessage("This caregiver is unavailable. Choose an available caregiver.");
      setSelectedAppointmentWorker(null);
      return;
    }

    try {
      setSchedulingAppointment(true);
      setAppointmentMessage("");

      const response = await axios.post(
        `${config.API_BASE_URL}/appointments`,
        {
          workerID: selectedAppointmentWorker.workerID,
          patientID,
          scheduledFor: new Date(scheduledFor).toISOString(),
          scheduledForLocal: scheduledFor,
          deviceTimeZone: getBrowserTimeZone() || deviceTimeZone,
          appointmentTimeZone: selectedSchedulingTimeZone,
          title: "WhatsApp care call",
          notes: appointmentNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setScheduledFor("");
      setAppointmentTimeZone(getBrowserTimeZone() || deviceTimeZone || "UTC");
      setAppointmentTimeZoneManuallyChanged(false);
      setAppointmentNotes("");
      setSelectedAppointmentWorker(null);
      setShowDiscoveryWhileScheduling(false);
      setAppointmentMessage("");
      setNotificationAlertMessage(
        response.data.message ||
          "Appointment scheduled. Confirmation emails were sent to you and the caregiver, and both will receive a ten-minute reminder."
      );
      fetchAppointments();
    } catch (err) {
      setAppointmentMessage(
        err.response?.data?.error || "Unable to schedule this appointment right now."
      );
    } finally {
      setSchedulingAppointment(false);
    }
  };

  const startEditAppointment = (appointment) => {
    const appointmentSchedulingTimeZone =
      appointment.patientTimeZoneAtScheduling ||
      appointment.patientTimeZone ||
      getBrowserTimeZone() ||
      deviceTimeZone ||
      "UTC";

    setSelectedAppointmentWorker(null);
    setAppointmentMessage("");
    setNotificationAlertMessage("");
    setEditingAppointmentID(appointment.appointmentID);
    setEditAppointmentTimeZone(appointmentSchedulingTimeZone);
    setEditScheduledFor(getAppointmentLocalInputValue(appointment, appointmentSchedulingTimeZone));
    setEditAppointmentNotes(appointment.notes || "");
    setEditAppointmentMessage("");
  };

  const closeAppointmentEdit = () => {
    setEditingAppointmentID("");
    setEditScheduledFor("");
    setEditAppointmentTimeZone(getBrowserTimeZone() || deviceTimeZone || "UTC");
    setEditAppointmentNotes("");
    setEditAppointmentMessage("");
    setUpdatingAppointmentID("");
  };

  const updateAppointment = async (event, appointment) => {
    event.preventDefault();

    const validationMessage = validateAppointmentDateTime(editScheduledFor, editAppointmentTimeZone);
    if (validationMessage) {
      setEditAppointmentMessage(validationMessage);
      return;
    }

    try {
      setUpdatingAppointmentID(appointment.appointmentID);
      setEditAppointmentMessage("");
      setNotificationAlertMessage("");

      const response = await axios.patch(
        `${config.API_BASE_URL}/appointments/${appointment.appointmentID}`,
        {
          patientID,
          scheduledFor: new Date(editScheduledFor).toISOString(),
          scheduledForLocal: editScheduledFor,
          deviceTimeZone: getBrowserTimeZone() || deviceTimeZone,
          appointmentTimeZone: editAppointmentTimeZone || getBrowserTimeZone() || deviceTimeZone,
          notes: editAppointmentNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNotificationAlertMessage(
        response.data.message ||
          "Appointment updated and the caregiver was notified by email."
      );
      closeAppointmentEdit();
      fetchAppointments();
    } catch (updateError) {
      setEditAppointmentMessage(
        updateError.response?.data?.error || "Unable to update that appointment right now."
      );
    } finally {
      setUpdatingAppointmentID("");
    }
  };

  const cancelAppointment = async (appointmentID) => {
    try {
      setCancellingAppointmentID(appointmentID);
      setNotificationAlertMessage("");
      const response = await axios.patch(
        `${config.API_BASE_URL}/appointments/${appointmentID}/cancel`,
        { patientID },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotificationAlertMessage(
        response.data.message || "Appointment cancelled and email alerts were sent."
      );
      fetchAppointments();
    } catch (cancelError) {
      setNotificationAlertMessage(
        cancelError.response?.data?.error || "Unable to cancel that appointment."
      );
    } finally {
      setCancellingAppointmentID("");
    }
  };

  const workerMarkers = useMemo(
    () =>
      workers
        .filter((worker) => worker.latitude && worker.longitude)
        .sort(
          (left, right) =>
            Number(right.status === "available") - Number(left.status === "available") ||
            getWorkerDisplayName(left).localeCompare(getWorkerDisplayName(right))
        ),
    [workers]
  );
  const availableWorkerCount = useMemo(
    () => workers.filter((worker) => worker.status === "available").length,
    [workers]
  );
  const unavailableWorkerCount = workers.length - availableWorkerCount;
  const normalizedNameSearch = nameSearch.trim().toLowerCase();
  const nameSearchResults = useMemo(() => {
    if (!normalizedNameSearch) {
      return [];
    }

    return workerMarkers
      .map((worker) => {
        const displayName = getWorkerDisplayName(worker);
        const normalizedName = displayName.toLowerCase();
        const matchIndex = normalizedName.indexOf(normalizedNameSearch);

        if (matchIndex === -1) {
          return null;
        }

        return {
          worker,
          displayName,
          matchIndex,
          startsWithMatch: normalizedName.startsWith(normalizedNameSearch)
        };
      })
      .filter(Boolean)
      .sort(
        (left, right) =>
          Number(right.startsWithMatch) - Number(left.startsWithMatch) ||
          left.matchIndex - right.matchIndex ||
          left.displayName.localeCompare(right.displayName)
      )
      .slice(0, 5);
  }, [normalizedNameSearch, workerMarkers]);
  const countryClusters = useMemo(
    () => buildCountryClusters(workerMarkers),
    [workerMarkers]
  );
  const visibleWorkers = useMemo(
    () =>
      expandedCountry
        ? workerMarkers.filter(
            (worker) => (worker.country || "Other") === expandedCountry
          )
        : [],
    [expandedCountry, workerMarkers]
  );
  const overviewMarkers = spreadOverlappingMarkers([
    ...countryClusters.map((cluster) => ({
      ...cluster,
      markerType: "cluster",
      markerID: `country-${cluster.country}`
    })),
    ...(patient?.latitude && patient?.longitude
      ? [
          {
            ...patient,
            markerType: "patient",
            markerID: "patient-home"
          }
        ]
      : [])
  ]);
  const expandedMarkers = spreadOverlappingMarkers([
    ...visibleWorkers.map((worker) => ({
      ...worker,
      markerType: "worker",
      markerID: worker.workerID
    })),
    ...(patient?.latitude && patient?.longitude
      ? [
          {
            ...patient,
            markerType: "patient",
            markerID: "patient-home"
          }
        ]
      : [])
  ]);
  const overviewCountryClusters = overviewMarkers.filter((marker) => marker.markerType === "cluster");
  const expandedWorkerMarkers = expandedMarkers.filter((marker) => marker.markerType === "worker");
  const expandedPatientMarker = expandedMarkers.find((marker) => marker.markerType === "patient");
  const overviewPatientMarker = overviewMarkers.find((marker) => marker.markerType === "patient");
  const overlapGuides = buildOverlapGuides(expandedWorkerMarkers);
  const appointmentNotifications = useMemo(
    () =>
      appointments.filter(
        (appointment) =>
          appointment.status !== "cancelled" &&
          (getAppointmentTime(appointment) || 0) >= Date.now() - APPOINTMENT_ALERT_WINDOW_MS
      ),
    [appointments]
  );

  useEffect(() => {
    const urgentAppointment = appointmentNotifications.find(
      (appointment) =>
        appointment.workerStatus !== "unavailable" &&
        getAppointmentAlertStatus(appointment) === "now"
    );

    setUrgentAppointmentID(urgentAppointment?.appointmentID || "");
  }, [appointmentNotifications]);

  useEffect(() => {
    if (!selectedWorkerID) return;

    const marker = workerMarkerRefs.current[selectedWorkerID];
    if (!marker) return;

    const timer = window.setTimeout(() => marker.openPopup(), 160);
    return () => window.clearTimeout(timer);
  }, [selectedWorkerID, expandedWorkerMarkers]);

  const handleCountryClusterClick = (cluster) => {
    setExpandedCountry(cluster.country);
    setFocusPoints(cluster.members.map((worker) => [worker.latitude, worker.longitude]));
  };

  const handleNameSearchSelect = (worker) => {
    setNameSearch(getWorkerDisplayName(worker));
    setExpandedCountry(worker.country || "Other");
    setSelectedWorkerID(worker.workerID);
    setFocusPoints([[Number(worker.latitude), Number(worker.longitude)]]);
  };

  const focusActivatedCaregiver = (caregiver) => {
    setSelectedAppointmentWorker(null);
    setShowDiscoveryWhileScheduling(true);
    setDraftFilters({
      language: "",
      gender: "",
      country: "",
      rate: "",
      availability: ""
    });
    setFilters({});
    setNameSearch(getWorkerDisplayName(caregiver));
    setExpandedCountry(caregiver.country || "Other");
    setSelectedWorkerID(caregiver.workerID);
    if (caregiver.latitude && caregiver.longitude) {
      setFocusPoints([[Number(caregiver.latitude), Number(caregiver.longitude)]]);
    }
  };

  const deactivateActivatedCaregiver = (caregiver) => {
    toggleActivation(caregiver.workerID, true);
  };

  const resetCountryView = () => {
    setExpandedCountry("");
    setSelectedWorkerID("");
    const allPoints = [
      ...countryClusters.map((cluster) => [cluster.latitude, cluster.longitude]),
      ...(patient?.latitude && patient?.longitude ? [[patient.latitude, patient.longitude]] : [])
    ];
    setFocusPoints(allPoints);
  };

  return (
    <>
      <style>{popupStyles}</style>

      <div style={page}>
        <nav className="elder-mobile-map-nav" aria-label="Map page shortcuts">
          <button
            type="button"
            onClick={() => filterPanelRef.current?.scrollIntoView({ behavior: "smooth" })}
          >
            Care &amp; schedule
          </button>
          <button
            type="button"
            onClick={() => mapSectionRef.current?.scrollIntoView({ behavior: "smooth" })}
          >
            View map
          </button>
        </nav>
        <div style={layout} className="elder-map-layout">
        <aside ref={filterPanelRef} className="elder-map-filter-panel" style={filterPanel}>
          <div className="elder-map-filter-header" style={headerRow}>
            <button type="button" style={backBtn} onClick={() => navigate("/elder-intro")}>
              ← Back to overview
            </button>
            <div>
              <div style={panelEyebrow}>Caregiver discovery</div>
              <h3 style={filterTitle}>{t("map:map.filterSmartTitle")}</h3>
            </div>
          </div>

          {selectedAppointmentWorker && (
            <div style={schedulingFocusCard}>
              <div style={schedulingFocusHeader}>
                <div>
                  <div style={panelEyebrow}>Selected caregiver</div>
                  <h3 style={schedulingFocusTitle}>
                    {getWorkerDisplayName(selectedAppointmentWorker)}
                  </h3>
                  <p style={schedulingFocusMeta}>
                    Set the appointment time first. Search and filters are below if you want another caregiver.
                  </p>
                </div>
                <button
                  type="button"
                  style={schedulingFocusToggle}
                  onClick={() => setShowDiscoveryWhileScheduling((previous) => !previous)}
                >
                  {showDiscoveryWhileScheduling ? "Hide search" : "Change caregiver"}
                </button>
              </div>

              <AppointmentScheduler
                worker={selectedAppointmentWorker}
                scheduledFor={scheduledFor}
                appointmentTimeZone={appointmentTimeZone}
                appointmentTimeZoneManuallyChanged={appointmentTimeZoneManuallyChanged}
                notes={appointmentNotes}
                scheduling={schedulingAppointment}
                deviceTimeZone={deviceTimeZone}
                message={appointmentMessage}
                onScheduledForChange={setScheduledFor}
                onAppointmentTimeZoneChange={handleAppointmentTimeZoneChange}
                onUseDeviceTimeZone={useDeviceAppointmentTimeZone}
                onNotesChange={setAppointmentNotes}
                onSubmit={scheduleAppointment}
                onCancel={() => {
                  setSelectedAppointmentWorker(null);
                  setShowDiscoveryWhileScheduling(false);
                  setAppointmentMessage("");
                  setAppointmentTimeZone(getBrowserTimeZone() || deviceTimeZone || "UTC");
                  setAppointmentTimeZoneManuallyChanged(false);
                }}
                panelStyle={schedulingFocusForm}
              />
            </div>
          )}

          <AppointmentNotifications
            appointments={appointmentNotifications}
            urgentAppointmentID={urgentAppointmentID}
            alertMessage={notificationAlertMessage}
            lastSyncedAt={lastAppointmentSync}
            syncError={appointmentSyncError}
            onCancelAppointment={cancelAppointment}
            cancellingAppointmentID={cancellingAppointmentID}
            onStartEditAppointment={startEditAppointment}
            editingAppointmentID={editingAppointmentID}
            editScheduledFor={editScheduledFor}
            editAppointmentTimeZone={editAppointmentTimeZone}
            editAppointmentNotes={editAppointmentNotes}
            editAppointmentMessage={editAppointmentMessage}
            updatingAppointmentID={updatingAppointmentID}
            onEditScheduledForChange={setEditScheduledFor}
            onEditAppointmentTimeZoneChange={setEditAppointmentTimeZone}
            onEditNotesChange={setEditAppointmentNotes}
            onSubmitAppointmentEdit={updateAppointment}
            onCloseAppointmentEdit={closeAppointmentEdit}
            deviceTimeZone={deviceTimeZone}
          />

          <ActivatedCaregiversPanel
            caregivers={activatedCaregivers}
            expanded={showActivatedCaregivers}
            onToggleExpanded={() => setShowActivatedCaregivers((previous) => !previous)}
            loadingWorkerID={loadingWorkerID}
            onSchedule={openScheduler}
            onFocus={focusActivatedCaregiver}
            onDeactivate={deactivateActivatedCaregiver}
          />

          {(!selectedAppointmentWorker || showDiscoveryWhileScheduling) && (
            <>
          <div style={field}>
            <label style={label} htmlFor="elder-map-name-search">
              Search by caregiver name
            </label>
            <input
              id="elder-map-name-search"
              type="text"
              style={input}
              value={nameSearch}
              onChange={(event) => setNameSearch(event.target.value)}
              placeholder="Type a first or last name"
              autoComplete="off"
            />

            {normalizedNameSearch && (
              <div style={nameSearchResultsPanel}>
                {nameSearchResults.length ? (
                  nameSearchResults.map(({ worker, displayName }) => (
                    <button
                      key={worker.workerID}
                      type="button"
                      style={nameSearchResultButton}
                      onClick={() => handleNameSearchSelect(worker)}
                    >
                      <span style={nameSearchResultName}>{displayName}</span>
                      <span style={nameSearchResultMeta}>
                        {getLocationLabel(worker) || worker.country || "Pinned map location"}
                      </span>
                    </button>
                  ))
                ) : (
                  <div style={nameSearchEmpty}>No caregiver found with that name.</div>
                )}
              </div>
            )}
          </div>

          <div style={filterBlock}>
            <div style={filterBlockHeader}>
              <span style={panelEyebrow}>Filters</span>
              <span style={filterHelpText}>Choose one or many, then apply.</span>
            </div>

            <div style={buttonRow}>
              <button type="button" style={applyBtn} onClick={applyFilter}>
                {t("common:buttons.apply")}
              </button>
              <button type="button" style={clearBtn} onClick={clearFilter}>
                {t("common:buttons.clear")}
              </button>
            </div>

            <div style={filterGrid}>
              {FILTER_TYPES.map((filter) => (
                <label key={filter.value} style={filterField}>
                  <span style={label}>{filter.label}</span>
                  <select
                    style={input}
                    value={draftFilters[filter.value] || ""}
                    onChange={(event) =>
                      setDraftFilters((previous) => ({
                        ...previous,
                        [filter.value]: event.target.value
                      }))
                    }
                  >
                    <option value="">{t("map:map.filterSelect")}</option>
                    {getFilterOptions(filter.value).map((valueOption) => (
                      <option key={valueOption.value} value={valueOption.value}>
                        {valueOption.label}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </div>

          {activeFilters.length > 0 && (
            <div style={activeFilterPanel}>
              <div style={activeFilterTitle}>Active filters</div>
              <div style={activeFilterList}>
                {activeFilters.map(([filterKey, filterValue]) => (
                  <span key={filterKey} style={activeFilterChip}>
                    {FILTER_TYPES.find((filter) => filter.value === filterKey)?.label || filterKey}:{" "}
                    {getFilterValueLabel(filterKey, filterValue)}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div style={availabilitySummaryCard}>
            <div style={availabilitySummaryItem}>
              <span style={{ ...availabilitySummaryDot, background: "#16a34a" }} />
              <strong>{availableWorkerCount}</strong>
              <span>available</span>
            </div>
            <div style={availabilitySummaryItem}>
              <span style={{ ...availabilitySummaryDot, background: "#dc2626" }} />
              <strong>{unavailableWorkerCount}</strong>
              <span>unavailable</span>
            </div>
            {patient?.latitude && patient?.longitude && (
              <button
                type="button"
                style={myLocationButton}
                onClick={() => setFocusPoints([[patient.latitude, patient.longitude]])}
              >
                Center on my location
              </button>
            )}
          </div>

          {activationError && <div style={activationErrorBox}>{activationError}</div>}

          {expandedCountry && (
            <div style={clusterInfoCard}>
              <strong style={clusterInfoTitle}>{expandedCountry}</strong>
              <p style={clusterInfoText}>
                Showing {visibleWorkers.length} caregiver{visibleWorkers.length === 1 ? "" : "s"} in this country.
              </p>
              <button type="button" style={clusterResetBtn} onClick={resetCountryView}>
                Show all country markers
              </button>
            </div>
          )}
            </>
          )}

        </aside>

        <section ref={mapSectionRef} style={mapFrame} className="elder-map-frame">
          <MapContainer
            center={[20, 0]}
            zoom={2}
            minZoom={2}
            zoomControl={false}
            preferCanvas
            zoomAnimation={false}
            fadeAnimation={false}
            markerZoomAnimation={false}
            worldCopyJump
            style={mapStyle}
            className="elder-map-canvas"
          >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            updateWhenIdle
            updateWhenZooming={false}
            keepBuffer={1}
            detectRetina={false}
          />

          <ZoomControls />
          <MapViewportController focusPoints={focusPoints} />

          {!expandedCountry &&
            overviewCountryClusters.map((cluster) => (
              <Marker
                key={`country-${cluster.country}`}
                position={[cluster.markerLatitude, cluster.markerLongitude]}
                icon={createCountryClusterIcon(cluster.country, cluster.members.length)}
                zIndexOffset={1200}
                eventHandlers={{
                  click: () => handleCountryClusterClick(cluster)
                }}
              >
                <Popup maxWidth={280} className="worker-map-popup">
                  <div className="country-cluster-popup">
                    <strong>{cluster.country}</strong>
                    <p>
                      Number shown on the marker: {cluster.members.length} caregiver{cluster.members.length === 1 ? "" : "s"} in {cluster.country}.
                    </p>
                    <p>
                      Abbreviation shown: {getCountryAbbreviation(cluster.country)}. Click the purple marker to spread caregivers across the country.
                    </p>
                  </div>
                </Popup>
              </Marker>
            ))}

          {expandedCountry &&
            overlapGuides.map((group) => (
              <Fragment key={`overlap-${group.key}`}>
                {group.members.map((worker) => (
                  <Polyline
                    key={`overlap-line-${worker.workerID}`}
                    positions={[group.center, [worker.markerLatitude, worker.markerLongitude]]}
                    pathOptions={{
                      color: worker.status === "available" ? "#16a34a" : "#dc2626",
                      weight: 2,
                      opacity: 0.55,
                      dashArray: "3 5"
                    }}
                  />
                ))}

                <CircleMarker
                  center={group.center}
                  radius={Math.min(10 + group.count, 16)}
                  pathOptions={{
                    color: "#0f5f58",
                    weight: 2,
                    fillColor: "#ffffff",
                    fillOpacity: 0.94
                  }}
                >
                  <Popup maxWidth={260} className="worker-map-popup">
                    <div className="country-cluster-popup">
                      <strong>Shared caregiver location</strong>
                      <p>
                        {group.count} caregivers share this same saved map point. Their markers are spread in a circle so they are easier to tap.
                      </p>
                    </div>
                  </Popup>
                </CircleMarker>
              </Fragment>
            ))}

          {expandedCountry &&
            expandedWorkerMarkers.map((worker) => (
              <Marker
                key={worker.workerID}
                position={[worker.markerLatitude, worker.markerLongitude]}
                icon={worker.status === "available" ? greenIcon : redIcon}
                zIndexOffset={worker.workerID === selectedWorkerID ? 1300 : 0}
                ref={(marker) => {
                  if (marker) {
                    workerMarkerRefs.current[worker.workerID] = marker;
                  } else {
                    delete workerMarkerRefs.current[worker.workerID];
                  }
                }}
              >
                <Popup maxWidth={420} className="worker-map-popup">
                  <WorkerPopup
                    worker={worker}
                    loadingWorkerID={loadingWorkerID}
                    onToggleActivation={toggleActivation}
                    onSchedule={openScheduler}
                    schedulerWorkerID=""
                    schedulerProps={null}
                    onWatchIntro={setIntroVideoWorker}
                    renderStars={renderStars}
                    t={t}
                  />
                </Popup>
              </Marker>
            ))}

          {!expandedCountry && overviewPatientMarker && (
            <Marker
              position={[overviewPatientMarker.markerLatitude, overviewPatientMarker.markerLongitude]}
              icon={blackIcon}
              zIndexOffset={200}
            >
              <Popup maxWidth={360} className="worker-map-popup">
                <PatientPopup patient={overviewPatientMarker} t={t} />
              </Popup>
            </Marker>
          )}

          {expandedCountry && expandedPatientMarker && (
            <Marker
              position={[expandedPatientMarker.markerLatitude, expandedPatientMarker.markerLongitude]}
              icon={blackIcon}
            >
              <Popup maxWidth={360} className="worker-map-popup">
                <PatientPopup patient={expandedPatientMarker} t={t} />
              </Popup>
            </Marker>
          )}
          </MapContainer>
        </section>
        <IntroVideoModal
          worker={introVideoWorker}
          onClose={() => setIntroVideoWorker(null)}
        />
        </div>
      </div>
    </>
  );
};

export default ElderMap;

const formatAppointmentDate = (value) => {
  if (!value) return "Time not set";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const page = {
  minHeight: "100vh",
  padding: "18px",
  background: "linear-gradient(180deg, #edf4f8 0%, #dfe9f2 100%)",
  fontSize: "16px"
};

const layout = {
  display: "grid",
  gridTemplateColumns: "minmax(320px, 380px) minmax(0, 1fr)",
  gap: "18px",
  alignItems: "start",
  minHeight: 0,
  maxWidth: "1500px",
  margin: "0 auto"
};

const mapFrame = {
  position: "sticky",
  top: "18px",
  height: "calc(100svh - 36px)",
  minHeight: "620px",
  boxSizing: "border-box",
  background: "rgba(255, 255, 255, 0.86)",
  padding: "12px",
  borderRadius: "20px",
  border: "1px solid rgba(14, 85, 128, 0.14)",
  boxShadow: "0 22px 48px rgba(17, 44, 77, 0.14)"
};

const mapStyle = {
  height: "100%",
  minHeight: 0,
  width: "100%",
  borderRadius: "14px",
  zIndex: 1
};

const filterPanel = {
  position: "sticky",
  top: "18px",
  maxHeight: "calc(100vh - 36px)",
  overflowY: "auto",
  overscrollBehavior: "contain",
  scrollbarGutter: "stable",
  padding: "18px",
  background: "linear-gradient(180deg, #0e5580 0%, #156893 100%)",
  border: "1px solid rgba(255, 208, 63, 0.16)",
  borderRadius: "20px",
  boxShadow: "0 22px 48px rgba(12, 42, 68, 0.16)",
  zIndex: 10
};

const headerRow = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "16px"
};

const backBtn = {
  minHeight: "42px",
  padding: "0 14px",
  border: "none",
  background: "#ffffff",
  color: "#0E5580",
  borderRadius: "12px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "900",
  boxShadow: "0 10px 20px rgba(0,0,0,0.12)"
};

const panelEyebrow = {
  color: "#ffe07d",
  fontSize: "11px",
  fontWeight: "900",
  letterSpacing: "0.08em",
  textTransform: "uppercase"
};

const filterTitle = {
  margin: 0,
  fontSize: "20px",
  fontWeight: "900",
  color: "#ffffff"
};

const field = {
  marginBottom: "14px",
  display: "flex",
  flexDirection: "column"
};

const label = {
  fontSize: "13px",
  fontWeight: "800",
  marginBottom: "6px",
  color: "#e5f3fb"
};

const input = {
  minHeight: "44px",
  padding: "10px 12px",
  fontSize: "15px",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  borderRadius: "12px",
  background: "#f8fbfe",
  color: "#123a59",
  minWidth: 0,
  width: "100%"
};

const appointmentCompactInput = {
  ...input,
  minHeight: "36px",
  padding: "7px 9px",
  fontSize: "14px",
  borderRadius: "10px"
};

const appointmentCompactTextarea = {
  ...appointmentCompactInput,
  minHeight: "44px",
  resize: "vertical"
};

const filterBlock = {
  marginTop: "8px",
  padding: "14px",
  borderRadius: "16px",
  background: "rgba(255, 255, 255, 0.1)",
  border: "1px solid rgba(255, 255, 255, 0.12)"
};

const filterBlockHeader = {
  display: "flex",
  justifyContent: "space-between",
  gap: "10px",
  alignItems: "center",
  marginBottom: "12px"
};

const filterHelpText = {
  color: "rgba(255, 255, 255, 0.78)",
  fontSize: "12px",
  fontWeight: "700",
  textAlign: "right"
};

const filterGrid = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "12px"
};

const filterField = {
  display: "flex",
  flexDirection: "column"
};

const appointmentDateField = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0
};

const appointmentTimezoneHelp = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "8px",
  marginTop: "-2px",
  color: "#486581",
  fontSize: "11px",
  fontWeight: "700",
  lineHeight: 1.35
};

const appointmentTimezoneResetButton = {
  border: "1px solid rgba(14, 85, 128, 0.18)",
  borderRadius: "999px",
  background: "#f0f8ff",
  color: "#0e5580",
  cursor: "pointer",
  fontSize: "11px",
  fontWeight: "900",
  padding: "5px 9px"
};

const appointmentSuggestionWrap = {
  display: "flex",
  flexWrap: "wrap",
  gap: "7px",
  marginTop: "9px"
};

const appointmentSuggestionButton = {
  minHeight: "34px",
  padding: "0 10px",
  borderRadius: "999px",
  border: "1px solid rgba(14, 85, 128, 0.18)",
  background: "#ffffff",
  color: "#0e5580",
  cursor: "pointer",
  fontSize: "12px",
  fontWeight: "800"
};

const appointmentConfirmationCard = {
  display: "grid",
  gap: "4px",
  marginTop: "10px",
  padding: "11px 12px",
  borderRadius: "12px",
  background: "#edf7f4",
  border: "1px solid rgba(15, 95, 88, 0.16)",
  color: "#334e68",
  fontSize: "12px",
  lineHeight: 1.4
};

const appointmentConfirmationLabel = {
  color: "#0f5f58",
  fontSize: "10px",
  fontWeight: "900",
  letterSpacing: "0.08em",
  textTransform: "uppercase"
};

const appointmentSameTimezoneNote = {
  marginTop: "4px",
  padding: "7px 8px",
  borderRadius: "9px",
  background: "#fff7ed",
  color: "#9a3412",
  border: "1px solid #fed7aa",
  fontWeight: "800"
};

const appointmentFormLabel = {
  ...label,
  color: "#102a43"
};

const appointmentNotesLabel = {
  ...appointmentFormLabel,
  display: "block",
  marginTop: "12px"
};

const appointmentCompactNotesLabel = {
  ...appointmentNotesLabel,
  marginTop: "8px",
  marginBottom: "4px"
};

const nameSearchResultsPanel = {
  marginTop: "8px",
  borderRadius: "10px",
  border: "1px solid rgba(14, 85, 128, 0.16)",
  overflow: "hidden",
  background: "#ffffff"
};

const nameSearchResultButton = {
  width: "100%",
  border: "none",
  borderBottom: "1px solid rgba(14, 85, 128, 0.1)",
  background: "#ffffff",
  color: "#102a43",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "3px",
  padding: "10px",
  textAlign: "left"
};

const nameSearchResultName = {
  fontSize: "14px",
  fontWeight: "800"
};

const nameSearchResultMeta = {
  color: "#486581",
  fontSize: "12px",
  lineHeight: 1.35
};

const nameSearchEmpty = {
  color: "#486581",
  fontSize: "13px",
  padding: "10px"
};

const buttonRow = {
  display: "flex",
  gap: "10px",
  marginTop: "15px"
};

const appointmentCompactButtonRow = {
  ...buttonRow,
  gap: "8px",
  marginTop: "10px"
};

const activeFilterPanel = {
  marginTop: "14px",
  padding: "12px",
  borderRadius: "14px",
  background: "rgba(255, 255, 255, 0.12)",
  border: "1px solid rgba(255, 255, 255, 0.16)"
};

const activeFilterTitle = {
  color: "#ffffff",
  fontSize: "13px",
  fontWeight: "900",
  marginBottom: "8px"
};

const activeFilterList = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px"
};

const activeFilterChip = {
  borderRadius: "999px",
  background: "#ffffff",
  color: "#0e5580",
  padding: "7px 10px",
  fontSize: "12px",
  fontWeight: "900"
};

const availabilitySummaryCard = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "8px",
  padding: "10px",
  borderRadius: "14px",
  background: "#f8fafc",
  border: "1px solid #e2e8f0"
};

const availabilitySummaryItem = {
  display: "flex",
  alignItems: "center",
  gap: "5px",
  color: "#486581",
  fontSize: "12px"
};

const availabilitySummaryDot = {
  width: "9px",
  height: "9px",
  borderRadius: "50%",
  flexShrink: 0
};

const myLocationButton = {
  gridColumn: "1 / -1",
  minHeight: "38px",
  border: "1px solid #cbd5e1",
  borderRadius: "10px",
  background: "#ffffff",
  color: "#0e5580",
  fontWeight: "800",
  cursor: "pointer"
};

const clusterInfoCard = {
  marginTop: "14px",
  padding: "14px",
  borderRadius: "14px",
  background: "rgba(91, 33, 182, 0.08)",
  border: "1px solid rgba(91, 33, 182, 0.18)"
};

const clusterInfoTitle = {
  display: "block",
  color: "#4c1d95",
  fontSize: "15px",
  marginBottom: "4px"
};

const clusterInfoText = {
  margin: "0 0 10px",
  color: "#5b4b78",
  fontSize: "13px",
  lineHeight: 1.5
};

const clusterResetBtn = {
  width: "100%",
  minHeight: "40px",
  borderRadius: "12px",
  border: "none",
  background: "linear-gradient(135deg, #6d28d9, #8b5cf6)",
  color: "#fff",
  cursor: "pointer",
  fontWeight: "700"
};

const activationErrorBox = {
  marginTop: "12px",
  padding: "10px 12px",
  borderRadius: "12px",
  background: "#fff1f0",
  border: "1px solid #f2c6c2",
  color: "#b42318",
  fontSize: "13px",
  lineHeight: 1.45
};

const appointmentCompactErrorBox = {
  ...activationErrorBox,
  marginTop: "8px",
  padding: "8px 10px",
  fontSize: "12px",
  lineHeight: 1.35
};

const appointmentPanel = {
  marginTop: "14px",
  padding: "14px",
  borderRadius: "14px",
  background: "#ffffff",
  border: "1px solid rgba(14, 85, 128, 0.14)",
  boxShadow: "0 14px 30px rgba(15, 23, 42, 0.12)",
  cursor: "default"
};

const popupAppointmentPanel = {
  ...appointmentPanel,
  gridColumn: "1 / -1",
  marginTop: 0,
  padding: "10px",
  borderRadius: "12px",
  background: "#f8fbfd",
  boxShadow: "none"
};

const schedulingFocusCard = {
  padding: "12px",
  borderRadius: "16px",
  background: "linear-gradient(180deg, #ffffff 0%, #f2fbf8 100%)",
  border: "2px solid rgba(255, 208, 63, 0.76)",
  boxShadow: "0 18px 38px rgba(3, 58, 84, 0.24)",
  marginBottom: "14px"
};

const schedulingFocusHeader = {
  display: "flex",
  justifyContent: "space-between",
  gap: "10px",
  alignItems: "flex-start",
  marginBottom: "10px"
};

const schedulingFocusTitle = {
  margin: "3px 0 4px",
  color: "#102a43",
  fontSize: "18px",
  fontWeight: "900"
};

const schedulingFocusMeta = {
  margin: 0,
  color: "#486581",
  fontSize: "12px",
  lineHeight: 1.4
};

const schedulingFocusToggle = {
  minHeight: "36px",
  borderRadius: "999px",
  border: "1px solid rgba(14, 85, 128, 0.18)",
  background: "#0e5580",
  color: "#ffffff",
  cursor: "pointer",
  fontSize: "12px",
  fontWeight: "900",
  padding: "0 12px",
  whiteSpace: "nowrap"
};

const schedulingFocusForm = {
  ...popupAppointmentPanel,
  background: "#ffffff",
  border: "1px solid rgba(15, 95, 88, 0.12)",
  padding: "12px"
};

const activatedPanel = {
  marginBottom: "14px",
  padding: "10px",
  borderRadius: "16px",
  background: "linear-gradient(135deg, rgba(250, 204, 21, 0.26), rgba(255, 255, 255, 0.12))",
  border: "1px solid rgba(250, 204, 21, 0.58)",
  boxShadow: "0 14px 30px rgba(202, 138, 4, 0.18)"
};

const activatedHeaderButton = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "10px",
  minHeight: "54px",
  padding: "0",
  border: "none",
  background: "transparent",
  textAlign: "left",
  cursor: "pointer"
};

const activatedHeaderMeta = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  flexShrink: 0
};

const activatedTitle = {
  margin: "3px 0 0",
  color: "#fff7cc",
  fontSize: "16px",
  fontWeight: "900"
};

const activatedEyebrow = {
  color: "#facc15",
  fontSize: "11px",
  fontWeight: "900",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  textShadow: "0 1px 2px rgba(0, 0, 0, 0.25)"
};

const activatedCountBadge = {
  minWidth: "34px",
  minHeight: "34px",
  borderRadius: "999px",
  background: "#facc15",
  color: "#3f2f03",
  fontSize: "14px",
  fontWeight: "900",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center"
};

const activatedChevron = {
  width: "30px",
  height: "30px",
  borderRadius: "999px",
  background: "rgba(250, 204, 21, 0.22)",
  color: "#fff7cc",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "18px",
  fontWeight: "900",
  lineHeight: 1
};

const activatedList = {
  display: "grid",
  gap: "9px",
  marginTop: "10px",
  maxHeight: "280px",
  overflowY: "auto",
  paddingRight: "2px"
};

const activatedCard = {
  display: "grid",
  gap: "8px",
  padding: "10px",
  borderRadius: "12px",
  background: "#ffffff",
  border: "1px solid rgba(14, 85, 128, 0.12)"
};

const activatedName = {
  display: "block",
  color: "#102a43",
  fontSize: "14px",
  fontWeight: "900"
};

const activatedMeta = {
  display: "block",
  marginTop: "3px",
  color: "#486581",
  fontSize: "12px",
  lineHeight: 1.35
};

const activatedStatus = {
  display: "inline-flex",
  marginTop: "7px",
  padding: "4px 8px",
  borderRadius: "999px",
  fontSize: "11px",
  fontWeight: "900"
};

const activatedActions = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "7px"
};

const activatedPrimaryButton = {
  minHeight: "34px",
  borderRadius: "9px",
  border: "none",
  background: "#0f5f58",
  color: "#ffffff",
  fontSize: "12px",
  fontWeight: "900",
  cursor: "pointer"
};

const activatedSecondaryButton = {
  ...activatedPrimaryButton,
  background: "#e0f2fe",
  color: "#075985",
  border: "1px solid #bae6fd"
};

const activatedDangerButton = {
  ...activatedPrimaryButton,
  gridColumn: "1 / -1",
  background: "#fff1f2",
  color: "#be123c",
  border: "1px solid #fecdd3"
};

const activatedEmpty = {
  margin: 0,
  color: "rgba(255, 255, 255, 0.82)",
  fontSize: "13px",
  lineHeight: 1.45
};

const appointmentEyebrow = {
  color: "#0f5f58",
  fontSize: "11px",
  fontWeight: "900",
  letterSpacing: "0.08em",
  textTransform: "uppercase"
};

const appointmentTitle = {
  margin: "4px 0 10px",
  color: "#102a43",
  fontSize: "16px",
  fontWeight: "900"
};

const appointmentCompactTitle = {
  ...appointmentTitle,
  margin: "2px 0 6px",
  fontSize: "15px"
};

const appointmentList = {
  display: "grid",
  gap: "10px"
};

const appointmentCard = {
  padding: "12px",
  borderRadius: "12px",
  background: "rgba(15, 95, 88, 0.07)",
  border: "1px solid rgba(15, 95, 88, 0.12)",
  display: "grid",
  gap: "6px"
};

const urgentAppointmentCard = {
  border: "2px solid #f97316",
  background: "linear-gradient(180deg, #fff7ed 0%, #ffffff 100%)",
  boxShadow: "0 16px 32px rgba(249, 115, 22, 0.18)"
};

const soonAppointmentCard = {
  border: "1px solid #facc15",
  background: "linear-gradient(180deg, #fefce8 0%, #ffffff 100%)"
};

const urgentBadge = {
  justifySelf: "start",
  borderRadius: "999px",
  background: "#f97316",
  color: "#ffffff",
  padding: "5px 9px",
  fontSize: "11px",
  fontWeight: "900",
  letterSpacing: "0.04em",
  textTransform: "uppercase"
};

const soonBadge = {
  ...urgentBadge,
  background: "#ca8a04"
};

const appointmentAlertBox = {
  borderRadius: "12px",
  background: "#fff7ed",
  color: "#9a3412",
  padding: "10px 12px",
  fontSize: "13px",
  lineHeight: 1.45,
  border: "1px solid #fed7aa",
  fontWeight: "900"
};

const alertHealthCard = {
  display: "flex",
  gap: "10px",
  alignItems: "flex-start",
  marginBottom: "10px",
  padding: "10px",
  borderRadius: "12px",
  background: "#f7fafc",
  border: "1px solid #e2e8f0"
};

const alertHealthDot = {
  width: "10px",
  height: "10px",
  marginTop: "4px",
  borderRadius: "50%",
  boxShadow: "0 0 0 4px rgba(15, 95, 88, 0.09)",
  flexShrink: 0
};

const alertHealthCopy = {
  display: "grid",
  gap: "3px",
  color: "#334e68",
  fontSize: "11px",
  lineHeight: 1.4
};

const appointmentPausedNotice = {
  marginTop: "6px",
  padding: "8px 10px",
  borderRadius: "9px",
  background: "#fff1f2",
  border: "1px solid #fecdd3",
  color: "#9f1239",
  fontSize: "12px",
  fontWeight: "800"
};

const appointmentCaregiver = {
  display: "block",
  color: "#102a43",
  fontSize: "14px"
};

const appointmentTime = {
  display: "block",
  marginTop: "4px",
  color: "#0f5f58",
  fontSize: "13px",
  fontWeight: "900"
};

const emailDeliveryBadge = {
  display: "inline-flex",
  marginTop: "8px",
  padding: "5px 8px",
  borderRadius: "999px",
  background: "#dcfce7",
  color: "#166534",
  fontSize: "11px",
  fontWeight: "900"
};

const appointmentCopy = {
  margin: "8px 0 0",
  color: "#486581",
  fontSize: "13px",
  lineHeight: 1.45
};

const appointmentCompactCopy = {
  ...appointmentCopy,
  margin: "0 0 8px",
  fontSize: "12px",
  lineHeight: 1.35
};

const appointmentAction = {
  marginTop: "10px",
  minHeight: "36px",
  borderRadius: "10px",
  background: "#25d366",
  color: "#ffffff",
  textDecoration: "none",
  fontWeight: "900",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 12px",
  fontSize: "13px"
};

const appointmentButtonRow = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "8px",
  marginTop: "6px"
};

const appointmentEditButton = {
  minHeight: "36px",
  borderRadius: "10px",
  border: "1px solid rgba(14, 85, 128, 0.2)",
  background: "#ffffff",
  color: "#0e5580",
  fontWeight: "900",
  cursor: "pointer",
  padding: "0 12px",
  fontSize: "12px"
};

const appointmentCancelButton = {
  minHeight: "36px",
  borderRadius: "10px",
  border: "1px solid #fecaca",
  background: "#ffffff",
  color: "#b91c1c",
  fontWeight: "800",
  cursor: "pointer",
  padding: "0 12px",
  fontSize: "12px"
};

const appointmentInlineEditor = {
  ...popupAppointmentPanel,
  marginTop: "8px",
  border: "1px solid rgba(14, 85, 128, 0.12)"
};

const appointmentEmpty = {
  color: "#486581",
  fontSize: "13px",
  lineHeight: 1.5,
  margin: 0
};

const videoBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.62)",
  zIndex: 4000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px"
};

const videoModal = {
  width: "min(860px, 96vw)",
  background: "#ffffff",
  borderRadius: "14px",
  padding: "16px",
  boxShadow: "0 28px 70px rgba(15, 23, 42, 0.34)"
};

const videoHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "14px",
  marginBottom: "12px"
};

const videoTitle = {
  margin: "3px 0 0",
  color: "#102a43",
  fontSize: "18px",
  fontWeight: "900"
};

const videoCloseButton = {
  minHeight: "36px",
  borderRadius: "10px",
  border: "1px solid rgba(14, 85, 128, 0.18)",
  background: "#ffffff",
  color: "#0e5580",
  cursor: "pointer",
  fontWeight: "900",
  padding: "0 12px"
};

const videoFrame = {
  width: "100%",
  aspectRatio: "16 / 9",
  border: 0,
  borderRadius: "10px",
  background: "#0f172a"
};

const videoExternalLink = {
  marginTop: "12px",
  minHeight: "40px",
  borderRadius: "10px",
  background: "#0e5580",
  color: "#ffffff",
  textDecoration: "none",
  fontWeight: "900",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 14px",
  fontSize: "13px"
};

const applyBtn = {
  flex: 1,
  padding: "12px",
  background: "linear-gradient(135deg,#0E5580,#1c7ed6)",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  fontWeight: "600"
};

const appointmentCompactApplyBtn = {
  ...applyBtn,
  minHeight: "38px",
  padding: "8px 10px"
};

const clearBtn = {
  flex: 1,
  padding: "12px",
  background: "#6c757d",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer"
};

const appointmentCompactClearBtn = {
  ...clearBtn,
  minHeight: "38px",
  padding: "8px 10px"
};

const zoomControls = {
  position: "absolute",
  top: "18px",
  right: "18px",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  zIndex: 2000
};

const zoomBtn = {
  width: "55px",
  height: "55px",
  fontSize: "24px",
  fontWeight: "800",
  borderRadius: "14px",
  border: "none",
  background: "#ffffff",
  boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
  cursor: "pointer"
};

const popupStyles = `
.elder-mobile-map-nav {
  display: none;
}

.elder-map-filter-panel,
.worker-popup-card {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.42) transparent;
}

.worker-popup-card {
  scrollbar-color: rgba(14, 85, 128, 0.38) transparent;
}

.elder-map-canvas {
  background: #dbeafe;
}

.care-map-marker-shell {
  background: transparent;
  border: none;
}

.care-map-marker {
  position: relative;
  width: 46px;
  height: 46px;
  border-radius: 16px 16px 16px 4px;
  transform: rotate(-45deg);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid #ffffff;
  box-shadow: 0 14px 26px rgba(15, 23, 42, 0.24);
}

.care-map-marker::after {
  content: "";
  position: absolute;
  inset: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
}

.care-map-marker span {
  position: relative;
  z-index: 1;
  transform: rotate(45deg);
  color: #ffffff;
  font-size: 0.88rem;
  font-weight: 900;
  line-height: 1;
}

.care-map-marker-available {
  background: linear-gradient(135deg, #15803d, #22c55e);
}

.care-map-marker-unavailable {
  background: linear-gradient(135deg, #b91c1c, #ef4444);
}

.care-map-marker-elder {
  background: linear-gradient(135deg, #111827, #475569);
}

@media (max-width: 980px) {
  .elder-mobile-map-nav {
    position: sticky;
    top: max(8px, env(safe-area-inset-top));
    z-index: 3200;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin: 0 0 10px;
    padding: 7px;
    border: 1px solid rgba(14, 85, 128, 0.14);
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(14px);
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.14);
  }

  .elder-mobile-map-nav button {
    min-height: 46px;
    border: 0;
    border-radius: 12px;
    background: #0e5580;
    color: #fff;
    font-size: 14px;
    font-weight: 900;
    touch-action: manipulation;
  }

  .elder-mobile-map-nav button:last-child {
    background: linear-gradient(135deg, #d4a017, #f1c40f);
    color: #17324d;
  }

  .elder-map-filter-panel,
  .elder-map-frame {
    scroll-margin-top: 78px;
  }

  .elder-map-layout {
    grid-template-columns: 1fr !important;
  }

  .elder-map-frame {
    position: static !important;
    height: auto !important;
    min-height: 0 !important;
  }

  .elder-map-filter-panel {
    position: static !important;
    max-height: none !important;
  }

  .elder-map-canvas {
    height: 64svh !important;
    min-height: 380px !important;
  }
}

@media (max-width: 620px) {
  .elder-map-filter-header {
    align-items: flex-start !important;
    flex-direction: column !important;
  }

  .worker-map-popup .leaflet-popup-content {
    min-width: min(82vw, 280px) !important;
  }

  .worker-popup-grid,
  .worker-popup-hero {
    grid-template-columns: 1fr !important;
  }
}

.country-cluster-icon-wrapper {
  background: transparent;
  border: none;
}

.country-cluster-icon {
  width: 62px;
  height: 62px;
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #6d28d9, #a855f7);
  color: #fff;
  border: 3px solid rgba(255, 255, 255, 0.95);
  box-shadow: 0 14px 24px rgba(91, 33, 182, 0.32);
  gap: 0.08rem;
  padding: 0.28rem 0.2rem;
}

.country-cluster-code {
  font-weight: 900;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  line-height: 1;
}

.country-cluster-count {
  font-weight: 900;
  font-size: 1.05rem;
  line-height: 1;
}

.country-cluster-caption {
  font-weight: 700;
  font-size: 0.46rem;
  line-height: 1;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

@media (max-width: 720px) {
  .country-cluster-icon {
    width: 56px;
    height: 56px;
  }

  .country-cluster-count {
    font-size: 0.96rem;
  }

  .country-cluster-caption {
    font-size: 0.42rem;
  }
}

.country-cluster-popup {
  padding: 0.35rem;
  color: #2e1065;
}

.country-cluster-popup strong {
  display: block;
  font-size: 1rem;
}

.country-cluster-popup p {
  margin: 0.35rem 0 0;
  font-size: 0.85rem;
  line-height: 1.45;
}

.worker-map-popup .leaflet-popup-content-wrapper {
  padding: 0;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 24px 45px rgba(15, 23, 42, 0.18);
}

.worker-map-popup .leaflet-popup-content {
  margin: 0;
  width: min(90vw, 420px) !important;
  min-width: 0;
}

.worker-map-popup .leaflet-popup-tip {
  width: 18px;
  height: 18px;
  box-shadow: none;
}

.worker-popup-card {
  padding: 0.72rem;
  background:
    radial-gradient(circle at top right, rgba(216, 140, 58, 0.15), transparent 34%),
    linear-gradient(180deg, #ffffff, #f7fbfc);
  color: #102a43;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.55rem;
  max-height: min(72vh, 640px);
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
}

.worker-popup-hero {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.7rem;
  align-items: center;
  grid-column: 1 / -1;
}

.worker-avatar {
  width: 48px;
  height: 48px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f5f58, #2f8f85);
  color: #fff;
  font-weight: 800;
  box-shadow: 0 14px 26px rgba(15, 95, 88, 0.18);
}

.worker-photo {
  object-fit: cover;
}

.patient-avatar {
  font-size: 0.86rem;
  background: linear-gradient(135deg, #1f2937, #475569);
}

.worker-name {
  font-size: 0.98rem;
  font-weight: 800;
}

.worker-role {
  margin-top: 0.2rem;
  font-size: 0.8rem;
  color: #627d98;
}

.worker-status-chip {
  padding: 0.38rem 0.65rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 800;
  white-space: nowrap;
}

.worker-popup-grid {
  grid-column: 1 / -1;
  display: flex;
  gap: 0.55rem;
  margin-top: 0;
  overflow-x: auto;
  overscroll-behavior-inline: contain;
  padding-bottom: 0.18rem;
  scrollbar-width: thin;
}

.worker-availability-guard {
  grid-column: 1 / -1;
  padding: 0.7rem;
  border-radius: 0.8rem;
  background: #fff1f2;
  border: 1px solid #fecdd3;
  color: #9f1239;
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1.4;
}

.worker-popup-grid-expanded {
  margin-top: 0.8rem;
}

.worker-popup-stat {
  flex: 0 0 136px;
  min-height: 74px;
  padding: 0.62rem;
  border-radius: 13px;
  background: rgba(15, 95, 88, 0.06);
  border: 1px solid rgba(15, 95, 88, 0.08);
}

.worker-popup-stat span,
.worker-popup-label {
  display: block;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #486581;
}

.worker-popup-stat strong {
  display: block;
  margin-top: 0.28rem;
  font-size: 0.82rem;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.worker-popup-location-note {
  display: block;
  margin-top: 0.35rem;
  color: #6b7280;
  font-size: 0.72rem;
  line-height: 1.4;
}

.worker-popup-section {
  grid-column: span 2;
  margin-top: 0;
}

.worker-popup-text {
  margin-top: 0.28rem;
  color: #243b53;
  line-height: 1.5;
  font-size: 0.88rem;
}

.worker-popup-list {
  margin-top: 0.32rem;
  display: grid;
  gap: 0.42rem;
}

.worker-popup-list-item {
  padding-left: 0.85rem;
  position: relative;
  color: #243b53;
  line-height: 1.45;
  font-size: 0.86rem;
}

.worker-popup-list-item::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.48rem;
  width: 0.36rem;
  height: 0.36rem;
  border-radius: 999px;
  background: #0f5f58;
}

.worker-rating-row {
  margin-top: 0.28rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.75rem;
}

.worker-rating-copy {
  font-size: 0.82rem;
  color: #627d98;
}

.worker-more-toggle {
  display: none;
  margin-top: 0.85rem;
  width: 100%;
  min-height: 46px;
  padding: 0.8rem 0.95rem;
  border: 1px solid rgba(15, 95, 88, 0.18);
  border-radius: 14px;
  background: rgba(15, 95, 88, 0.08);
  color: #0f5f58;
  font-size: 0.88rem;
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  transition: background 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
}

.worker-more-toggle:hover,
.worker-more-toggle:focus-visible {
  background: rgba(15, 95, 88, 0.14);
  border-color: rgba(15, 95, 88, 0.28);
  transform: translateY(-1px);
}

.worker-more-toggle-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 999px;
  background: rgba(15, 95, 88, 0.12);
  font-size: 0.84rem;
  line-height: 1;
}

.worker-more-toggle-icon.is-open {
  background: rgba(15, 95, 88, 0.18);
}

.worker-primary-action,
.worker-secondary-action {
  width: 100%;
  margin-top: 0;
  min-height: 44px;
  border: none;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-weight: 800;
  font-size: 0.86rem;
  cursor: pointer;
}

.worker-primary-action {
  background: linear-gradient(135deg, #25d366, #159947);
  color: #fff;
  box-shadow: 0 16px 28px rgba(21, 153, 71, 0.18);
}

.worker-secondary-action {
  background: linear-gradient(135deg, #0e5580, #1c7ed6);
  color: #fff;
  box-shadow: 0 16px 28px rgba(15, 23, 42, 0.12);
}

.worker-popup-card > .worker-primary-action {
  grid-column: span 2;
}

.worker-popup-card > .worker-secondary-action {
  grid-column: span 1;
}

.worker-popup-card > .appointment-scheduler-form {
  grid-column: 1 / -1;
}

.appointment-scheduler-form-compact {
  align-self: start;
}

.appointment-scheduler-form-compact textarea {
  max-height: 76px;
}

.worker-disabled {
  background: #cbd5e1;
  color: #475569;
  box-shadow: none;
  cursor: not-allowed;
}

@media (max-width: 980px) {
  .worker-map-popup .leaflet-popup-content {
    min-width: 245px;
  }
}

@media (max-width: 720px) {
  .elder-map-filter-panel {
    position: fixed !important;
    left: 0.5rem !important;
    right: 0.5rem !important;
    bottom: calc(0.5rem + env(safe-area-inset-bottom)) !important;
    top: auto !important;
    width: auto !important;
    max-width: none !important;
    max-height: min(54svh, 470px) !important;
    padding: 0.85rem !important;
    border-radius: 18px !important;
    cursor: default !important;
  }

  .elder-map-filter-header {
    cursor: default !important;
  }

  .elder-map-filter-panel select,
  .elder-map-filter-panel input,
  .elder-map-filter-panel button,
  .elder-map-filter-panel textarea {
    font-size: 16px !important;
  }

  .leaflet-control-attribution {
    max-width: calc(100vw - 1rem);
    white-space: normal;
  }

  .worker-map-popup .leaflet-popup-content-wrapper {
    border-radius: 18px;
  }

  .worker-map-popup .leaflet-popup-content {
    min-width: 0;
    width: min(92vw, 340px) !important;
  }

  .worker-popup-card {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    max-height: min(58svh, 520px);
    overflow-y: auto;
    padding: 0.72rem;
    gap: 0.55rem;
  }

  .worker-popup-hero {
    grid-template-columns: auto 1fr;
  }

  .worker-status-chip {
    grid-column: 1 / -1;
    justify-self: start;
  }

  .worker-popup-grid {
    display: flex;
  }

  .worker-rating-row {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.3rem;
  }

  .worker-popup-section {
    grid-column: 1 / -1;
  }

  .worker-popup-stat {
    flex-basis: 126px;
    min-height: 68px;
  }

  .worker-popup-card > .worker-primary-action,
  .worker-popup-card > .worker-secondary-action {
    grid-column: span 1;
    min-height: 42px;
    font-size: 0.78rem;
    padding-inline: 0.45rem;
  }

  .appointment-date-time-grid {
    grid-template-columns: 1fr !important;
  }

  .appointment-scheduler-form-compact .appointment-date-time-grid {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
  }

  .appointment-scheduler-form-compact .appointment-date-time-grid > label:first-child {
    grid-column: 1 / -1;
  }
}

@media (max-width: 420px) {
  .elder-map-filter-panel {
    max-height: min(48svh, 390px) !important;
  }

  .worker-map-popup .leaflet-popup-content {
    width: min(84vw, 292px) !important;
  }
}
`;
